import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from 'sonner';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { subscribeToNotifications } from './utils/pushNotifications';

// ============== ACCESSIBILITY COMPONENTS ==============
const SkipLink = () => (
  <a 
    href="#main-content" 
    className="skip-link"
    aria-label="Aller au contenu principal"
  >
    Aller au contenu principal
  </a>
);

// Live region for screen reader announcements
const LiveAnnouncer = ({ message }) => (
  <div 
    role="status" 
    aria-live="polite" 
    aria-atomic="true"
    className="sr-only"
  >
    {message}
  </div>
);

// ============== CONTEXT ==============
const AuthContext = createContext(null);
const LanguageContext = createContext(null);
const NotificationContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Préférences par défaut envoyées lors de la première demande d'autorisation
// notifications (mêmes clés que ProfileSettingsPage) — Élodie #240297.
const DEFAULT_NOTIF_PREFS = {
  match_found: true,
  match_message: true,
  private_messages: true,
  event_nearby: true,
  forum_replies: true,
  event_reminders: true,
  crush_received: true,
  service_requests: true
};

// ============== TRANSLATIONS ==============
const translations = {
  fr: {
    welcome: "Rejoins le club MamaLink",
    subtitle: "Trouve ta meilleure amie de maternité",
    login: "Je me connecte",
    register: "Créer un compte",
    email: "Email",
    password: "Mot de passe",
    firstName: "Prénom",
    alreadyHaveAccount: "Déjà un compte ?",
    noAccount: "Pas encore de compte ?",
    forum: "Forums",
    bonPlans: "Bons Plans",
    conseils: "Conseils",
    ateliers: "Ateliers",
    events: "Événements",
    urgences: "Urgences",
    match: "Mon Match",
    messages: "Messages",
    profile: "Profil",
    logout: "Déconnexion",
    findMatch: "Trouver mon match parfait",
    startQuiz: "Commencer le questionnaire",
    yourName: "Ton prénom",
    yourAge: "Ton âge",
    yourCity: "Ta ville",
    pregnancyPath: "Ton parcours de grossesse",
    natural: "Grossesse naturelle",
    pma: "Parcours PMA",
    adoption: "Adoption",
    next: "Suivant",
    previous: "Précédent",
    submit: "Valider",
    loading: "Chargement...",
    noMatchYet: "Pas encore de match",
    matchFound: "Match trouvé !",
    compatibility: "Compatibilité",
    startConversation: "Commencer la conversation",
    typeMessage: "Écrire un message...",
    send: "Envoyer",
    call: "Appeler",
    videoCall: "Appel vidéo",
    dailyMessage: "Message du jour",
    emergencyNumbers: "Numéros d'urgence",
    workshops: "Ateliers en visio",
    registerWorkshop: "S'inscrire",
    registered: "Inscrit",
    createEvent: "Créer un événement",
    interested: "Intéressée",
    attending: "Je viens",
    participants: "participants",
    shareTip: "Partager un conseil",
    shareGoodDeal: "Partager un bon plan",
    createPost: "Créer un post",
    comments: "commentaires",
    likes: "j'aime",
    language: "Langue",
    settings: "Paramètres"
  },
  en: {
    welcome: "Join the MamaLink club",
    subtitle: "Find your best maternity friend",
    login: "Log in",
    register: "Create account",
    email: "Email",
    password: "Password",
    firstName: "First name",
    alreadyHaveAccount: "Already have an account?",
    noAccount: "Don't have an account?",
    forum: "Forums",
    bonPlans: "Good Deals",
    conseils: "Tips",
    ateliers: "Workshops",
    events: "Events",
    urgences: "Emergency",
    match: "My Match",
    messages: "Messages",
    profile: "Profile",
    logout: "Logout",
    findMatch: "Find my perfect match",
    startQuiz: "Start questionnaire",
    yourName: "Your name",
    yourAge: "Your age",
    yourCity: "Your city",
    pregnancyPath: "Your pregnancy path",
    natural: "Natural pregnancy",
    pma: "IVF/PMA path",
    adoption: "Adoption",
    next: "Next",
    previous: "Previous",
    submit: "Submit",
    loading: "Loading...",
    noMatchYet: "No match yet",
    matchFound: "Match found!",
    compatibility: "Compatibility",
    startConversation: "Start conversation",
    typeMessage: "Write a message...",
    send: "Send",
    call: "Call",
    videoCall: "Video call",
    dailyMessage: "Daily message",
    emergencyNumbers: "Emergency numbers",
    workshops: "Video workshops",
    registerWorkshop: "Register",
    registered: "Registered",
    createEvent: "Create event",
    interested: "Interested",
    attending: "I'm coming",
    participants: "participants",
    shareTip: "Share a tip",
    shareGoodDeal: "Share a good deal",
    createPost: "Create post",
    comments: "comments",
    likes: "likes",
    language: "Language",
    settings: "Settings"
  },
  es: {
    welcome: "Únete al club MamaLink",
    subtitle: "Encuentra tu mejor amiga de maternidad",
    login: "Iniciar sesión",
    register: "Crear cuenta",
    email: "Email",
    password: "Contraseña",
    firstName: "Nombre",
    alreadyHaveAccount: "¿Ya tienes cuenta?",
    noAccount: "¿No tienes cuenta?",
    forum: "Foro",
    bonPlans: "Ofertas",
    conseils: "Consejos",
    ateliers: "Talleres",
    events: "Eventos",
    urgences: "Urgencias",
    match: "Mi Match",
    messages: "Mensajes",
    profile: "Perfil",
    logout: "Cerrar sesión",
    findMatch: "Encontrar mi match perfecto",
    startQuiz: "Empezar el cuestionario",
    yourName: "Tu nombre",
    yourAge: "Tu edad",
    yourCity: "Tu ciudad",
    pregnancyPath: "Tu camino de embarazo",
    natural: "Embarazo natural",
    pma: "Reproducción asistida",
    adoption: "Adopción",
    next: "Siguiente",
    previous: "Anterior",
    submit: "Enviar",
    loading: "Cargando...",
    noMatchYet: "Aún sin match",
    matchFound: "¡Match encontrado!",
    compatibility: "Compatibilidad",
    startConversation: "Iniciar conversación",
    typeMessage: "Escribe un mensaje...",
    send: "Enviar",
    call: "Llamar",
    videoCall: "Videollamada",
    dailyMessage: "Mensaje del día",
    emergencyNumbers: "Números de emergencia",
    workshops: "Talleres en video",
    registerWorkshop: "Inscribirse",
    registered: "Inscrita",
    createEvent: "Crear evento",
    interested: "Interesada",
    attending: "Voy",
    participants: "participantes",
    shareTip: "Compartir un consejo",
    shareGoodDeal: "Compartir una oferta",
    createPost: "Crear post",
    comments: "comentarios",
    likes: "me gusta",
    language: "Idioma",
    settings: "Configuración"
  },
  it: {
    welcome: "Unisciti al club MamaLink",
    subtitle: "Trova la tua migliore amica di maternità",
    login: "Accedi",
    register: "Crea account",
    email: "Email",
    password: "Password",
    firstName: "Nome",
    alreadyHaveAccount: "Hai già un account?",
    noAccount: "Non hai un account?",
    forum: "Forums",
    bonPlans: "Offerte",
    conseils: "Consigli",
    ateliers: "Laboratori",
    events: "Eventi",
    urgences: "Emergenze",
    match: "Il mio Match",
    messages: "Messaggi",
    profile: "Profilo",
    logout: "Esci",
    findMatch: "Trova il mio match perfetto",
    startQuiz: "Inizia il questionario",
    yourName: "Il tuo nome",
    yourAge: "La tua età",
    yourCity: "La tua città",
    pregnancyPath: "Il tuo percorso di gravidanza",
    natural: "Gravidanza naturale",
    pma: "Procreazione assistita",
    adoption: "Adozione",
    next: "Avanti",
    previous: "Indietro",
    submit: "Invia",
    loading: "Caricamento...",
    noMatchYet: "Nessun match ancora",
    matchFound: "Match trovato!",
    compatibility: "Compatibilità",
    startConversation: "Inizia conversazione",
    typeMessage: "Scrivi un messaggio...",
    send: "Invia",
    call: "Chiama",
    videoCall: "Videochiamata",
    dailyMessage: "Messaggio del giorno",
    emergencyNumbers: "Numeri di emergenza",
    workshops: "Laboratori video",
    registerWorkshop: "Iscriviti",
    registered: "Iscritta",
    createEvent: "Crea evento",
    interested: "Interessata",
    attending: "Vengo",
    participants: "partecipanti",
    shareTip: "Condividi un consiglio",
    shareGoodDeal: "Condividi un'offerta",
    createPost: "Crea post",
    comments: "commenti",
    likes: "mi piace",
    language: "Lingua",
    settings: "Impostazioni"
  }
};

// ============== HOOKS ==============
const useAuth = () => useContext(AuthContext);
const useLanguage = () => useContext(LanguageContext);
const useNotifications = () => useContext(NotificationContext);

// ============== AUTH PROVIDER ==============
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Priorité au localStorage (rememberMe = true)
    const localToken = localStorage.getItem('mamalink_token');
    if (localToken) return localToken;
    return sessionStorage.getItem('mamalink_token');
  });
  const [loading, setLoading] = useState(true);
  const refreshTimeoutRef = useRef(null);
  const isCheckingAuth = useRef(false);

  // Fonction pour nettoyer la session
  const clearSession = useCallback(() => {
    localStorage.removeItem('mamalink_token');
    sessionStorage.removeItem('mamalink_token');
    setToken(null);
    setUser(null);
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
  }, []);

  // Synchroniser le token entre les onglets
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'mamalink_token') {
        if (e.newValue) {
          setToken(e.newValue);
        } else {
          // Token supprimé dans un autre onglet
          setToken(null);
          setUser(null);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Capture le prompt d'installation PWA (Android Chrome / Edge / Samsung Internet)
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      window.deferredInstallPrompt = e;
      console.log('[PWA] Install prompt captured and ready');
    };
    window.addEventListener('beforeinstallprompt', handler);
    // Si l'app est déjà installée, on le note
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] MamaLink installed!');
      window.deferredInstallPrompt = null;
    });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Re-vérifier l'auth quand la fenêtre reprend le focus (après retour d'un onglet)
  useEffect(() => {
    const handleFocus = () => {
      // Vérifier si le token est toujours valide au retour sur l'onglet
      const currentToken = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
      if (currentToken && currentToken !== token) {
        setToken(currentToken);
      } else if (!currentToken && token) {
        clearSession();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [token, clearSession]);

  // Fonction pour rafraîchir le token
  const refreshToken = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.post(`${API}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newToken = response.data.access_token;
      
      // Déterminer où stocker le token
      if (localStorage.getItem('mamalink_token')) {
        localStorage.setItem('mamalink_token', newToken);
      } else {
        sessionStorage.setItem('mamalink_token', newToken);
      }
      
      setToken(newToken);
      console.log('Token refreshed successfully');
      
      // Programmer le prochain refresh (6 jours avant expiration de 7 jours)
      scheduleTokenRefresh(response.data.expires_in);
      
      return newToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (error.response?.status === 401) {
        clearSession();
        toast.error('Session expirée, veuillez vous reconnecter');
      }
      return null;
    }
  }, [token, clearSession]);

  // Programmer le refresh automatique
  const scheduleTokenRefresh = useCallback((expiresIn) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    // Refresh 1 jour avant expiration (en millisecondes)
    // expiresIn est en secondes, on refresh 1 jour (86400s) avant
    const refreshTime = Math.max((expiresIn - 86400) * 1000, 60000); // minimum 1 minute
    
    refreshTimeoutRef.current = setTimeout(() => {
      refreshToken();
    }, refreshTime);
    
    console.log(`Token refresh scheduled in ${Math.round(refreshTime / 3600000)} hours`);
  }, [refreshToken]);

  useEffect(() => {
    const checkAuth = async () => {
      if (isCheckingAuth.current) return;
      isCheckingAuth.current = true;
      
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          
          // Programmer le refresh du token (6 jours = 518400 secondes)
          scheduleTokenRefresh(518400);

          // 🎯 Élodie #1150 — On NE demande PLUS automatiquement la permission au
          // démarrage. Le prompt navigateur est intrusif s'il apparaît sans contexte
          // (l'utilisatrice ne comprend pas pourquoi on lui demande). La demande de
          // permission est désormais UNIQUEMENT déclenchée par un clic explicite sur
          // "Activer les notifications" (ProfileSettingsPage), avec une explication
          // in-app avant le prompt.
          //
          // 🎯 Élodie #998 — Si la permission est DÉJÀ accordée, on rafraîchit
          // silencieusement l'abonnement à chaque ouverture. Ceci corrige le bug
          // "les rappels d'hydratation ne partent plus quand l'app est fermée" :
          // l'abonnement devient périodiquement stale (rotation VAPID, updates
          // Chrome, cleanup Android...) et le backend n'est pas notifié → il
          // continue d'envoyer sur un endpoint mort. Re-upsert idempotent au boot.
          if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            subscribeToNotifications(token, DEFAULT_NOTIF_PREFS, { silent: true }).catch(() => {});
          }

          // Notifications intelligentes (mood + comportement) — best-effort
          axios.post(`${API}/notifications/smart-check`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => {});
        } catch (error) {
          // Seulement si erreur 401/403 explicite, nettoyer la session
          if (error.response?.status === 401 || error.response?.status === 403) {
            // Double vérification avant de déconnecter
            const currentToken = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
            if (currentToken === token) {
              clearSession();
            }
          }
          // Pour les autres erreurs (réseau, 500), garder le token et réessayer plus tard
        }
      }
      setLoading(false);
      isCheckingAuth.current = false;
    };
    checkAuth();
    
    // Cleanup
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [token, clearSession, scheduleTokenRefresh]);

  // Intercepteur axios global pour gérer les tokens expirés et les refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        
        // Si erreur 401 et on n'a pas encore tenté le refresh
        if (error.response?.status === 401 && token && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            const newToken = await refreshToken();
            if (newToken) {
              // Retry la requête originale avec le nouveau token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            clearSession();
            toast.error('Session expirée, veuillez vous reconnecter');
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    return () => axios.interceptors.response.eject(interceptor);
  }, [token, clearSession, refreshToken]);

  // 🤖 Élodie #1022 — Enregistre/re-enregistre le token FCM côté app Android
  // dès qu'un JWT valide est disponible (login OU cold-start avec token stocké).
  // Le bridge n'existe que dans l'app native (WebView Android) — silent no-op sur web.
  useEffect(() => {
    if (!token) return;
    if (typeof window === 'undefined' || !window.MamaLinkAndroid) return;
    try {
      if (typeof window.MamaLinkAndroid.setAuth === 'function') {
        window.MamaLinkAndroid.setAuth(token, BACKEND_URL || '');
      }
      if (typeof window.MamaLinkAndroid.registerFcm === 'function') {
        window.MamaLinkAndroid.registerFcm();
      }
    } catch (e) { /* ignore */ }
  }, [token]);

  // Heartbeat de présence en ligne — pingue toutes les 60s tant que l'onglet est visible
  // pour que les autres mamans voient "En ligne" / "vue à l'instant" en temps réel.
  // Au unmount / changement de visibility => on bascule is_online=false.
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const ping = async (isOnline) => {
      if (cancelled) return;
      try {
        await axios.put(`${API}/profile/online-status?is_online=${isOnline ? 'true' : 'false'}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (_e) { /* silent : ne pas spammer en cas d'offline réseau */ }
    };
    // Premier ping immédiat
    ping(document.visibilityState === 'visible');
    // Heartbeat régulier
    const interval = setInterval(() => {
      ping(document.visibilityState === 'visible');
    }, 60 * 1000);
    // Si la maman quitte / cache l'app on coupe immédiatement
    const onVisibility = () => ping(document.visibilityState === 'visible');
    const onUnload = () => {
      try {
        // 🩹 Élodie #787 (health check) : sendBeacon envoie une POST par défaut, or notre endpoint est PUT-only → 405 pollue la console.
        //    fetch avec keepalive:true survit à la navigation/fermeture ET permet de choisir la méthode HTTP (PUT).
        const url = `${API}/profile/online-status?is_online=false`;
        const token = localStorage.getItem('token');
        fetch(url, {
          method: 'PUT',
          keepalive: true,
          headers: token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' },
          body: '{}',
        }).catch(() => { /* noop */ });
      } catch (_e) { /* noop */ }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onUnload);
      window.removeEventListener('beforeunload', onUnload);
      // Marque offline au démontage (logout)
      ping(false);
    };
  }, [token]);

  const login = async (email, password, rememberMe = true) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (rememberMe) {
      localStorage.setItem('mamalink_token', response.data.access_token);
    } else {
      sessionStorage.setItem('mamalink_token', response.data.access_token);
    }
    setToken(response.data.access_token);
    setUser(response.data.user);
    
    // Programmer le refresh
    scheduleTokenRefresh(168 * 3600); // 7 jours en secondes

    // 🎯 Élodie #1150 — Plus d'appel automatique à la permission au login.
    //   → uniquement re-subscribe silencieux si permission déjà accordée
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      subscribeToNotifications(response.data.access_token, DEFAULT_NOTIF_PREFS, { silent: true }).catch(() => {});
    }

    // 🤖 Élodie #1022 — Si on tourne dans l'app Android native, enregistre le token FCM
    try {
      if (typeof window !== 'undefined' && window.MamaLinkAndroid && typeof window.MamaLinkAndroid.setAuth === 'function') {
        window.MamaLinkAndroid.setAuth(response.data.access_token, BACKEND_URL || '');
        if (typeof window.MamaLinkAndroid.registerFcm === 'function') {
          window.MamaLinkAndroid.registerFcm();
        }
      }
    } catch (e) { /* bridge indisponible en web pur, on ignore */ }
    
    return response.data;
  };

  const register = async (email, password, firstName, language, referralCode = null) => {
    const url = referralCode 
      ? `${API}/auth/register?referral_code=${referralCode}`
      : `${API}/auth/register`;
    
    const response = await axios.post(url, { 
      email, password, first_name: firstName, language 
    });
    // ⚠️ Vérification email obligatoire : on NE log PAS automatiquement.
    // L'utilisatrice doit cliquer sur le lien reçu par email avant de se connecter.
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('mamalink_token');
    sessionStorage.removeItem('mamalink_token');
    setToken(null);
    setUser(null);
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    // 🤖 Élodie #1022 — Efface le JWT côté app Android
    try {
      if (typeof window !== 'undefined' && window.MamaLinkAndroid && typeof window.MamaLinkAndroid.clearAuth === 'function') {
        window.MamaLinkAndroid.clearAuth();
      }
    } catch (e) { /* bridge indisponible en web pur */ }
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  // Refresh user data from server
  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, updateUser, refreshUser, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============== LANGUAGE PROVIDER ==============
const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem('mamalink_lang') || 'fr');

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.fr[key] || key;
  }, [language]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('mamalink_lang', lang);
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// ============== COMPONENTS ==============
import { Heart, MessageCircle, Calendar, MapPin, Users, Phone, Video, Send, ArrowLeft, ArrowRight, Check, Star, Sparkles, Baby, Activity, Home, Settings, LogOut, Menu, X, Plus, ThumbsUp, Clock, User, Globe, Bell, Award, Camera, Smile, Target, Shield, Eye, EyeOff, Share2, Gift, MessageSquare, Tag, Bookmark, BookOpen, Brain, Moon, Flame, Ambulance, Crown, HelpCircle, Mail, Lock, AlertCircle, Loader2 } from 'lucide-react';

// Import logo
import logoImg from './assets/logo.png';

// Import SocialShare component
import SocialShare from './components/SocialShare';

// Import ReminderModals component
import { ReminderModals } from './components/ReminderModals';

// Import OnboardingTutorial component
import OnboardingTutorial from './components/OnboardingTutorial';
import { OnboardingChecklist } from './components/OnboardingChecklist';
import LaunchPromoBanner from './components/LaunchPromoBanner';
import PlannerLaunchTeaser from './components/PlannerLaunchTeaser';
import { SocialProofBanner } from './components/SocialProofBanner';
import { ContextualUpsellModal } from './components/ContextualUpsell';
import { AppConfirmHost } from './components/AppConfirm';
import StarterPackWelcomeModal from './components/StarterPackWelcomeModal';
import StarterPackHomeBanner from './components/StarterPackHomeBanner';
import NewMembersWidget from './components/NewMembersWidget';
import NotificationPermissionCard from './components/NotificationPermissionCard';

// Landing Page with Language Selection
const LandingPage = () => {
  const { t, language, changeLanguage } = useLanguage();
  const { user, loading } = useAuth();
  const { highContrast, toggleHighContrast, largeText, toggleLargeText } = useTheme();
  const navigate = useNavigate();
  // Show language selector only if: not logged in AND never selected a language before
  const hasSelectedLanguage = localStorage.getItem('mamalink_lang_selected') || localStorage.getItem('mamalink_lang');
  const [showLangSelector, setShowLangSelector] = useState(!hasSelectedLanguage);
  const [showAccessibility, setShowAccessibility] = useState(false);

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleLanguageSelect = (lang) => {
    changeLanguage(lang);
    localStorage.setItem('mamalink_lang_selected', 'true');
    setShowLangSelector(false);
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FCD5CE] via-[#FDE2E4] to-[#FFF1E6] flex items-center justify-center">
        <Heart className="w-16 h-16 text-[#FFB5A7] animate-pulse" />
      </div>
    );
  }

  // Initial language selection modal
  if (showLangSelector) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FCD5CE] via-[#FDE2E4] to-[#FFF1E6] flex flex-col items-center justify-center p-6">
        {/* Accessibility Button - Fixed position */}
        <button
          onClick={() => setShowAccessibility(!showAccessibility)}
          className="fixed top-4 right-4 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all z-50 border-2 border-[#A2D2FF]"
          aria-label="Options d'accessibilité"
          aria-expanded={showAccessibility}
          data-testid="lang-accessibility-toggle"
        >
          <Eye className="w-6 h-6 text-[#A2D2FF]" aria-hidden="true" />
        </button>

        {/* Accessibility Panel */}
        {showAccessibility && (
          <div className="fixed top-16 right-4 bg-white rounded-2xl shadow-xl p-4 z-50 w-72 border-2 border-[#A2D2FF]" role="dialog" aria-label="Options d'accessibilité">
            <h2 className="font-bold text-[#5C3D36] mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#A2D2FF]" aria-hidden="true" />
              Accessibilité
            </h2>
            
            {/* High Contrast Toggle */}
            <div className="flex items-center justify-between py-3 border-b border-[#F8EDEB]">
              <div>
                <p className="font-medium text-[#5C3D36]">Contraste élevé</p>
                <p className="text-xs text-[#9D8189]">Pour malvoyants</p>
              </div>
              <button
                onClick={toggleHighContrast}
                className={`relative w-12 h-7 rounded-full transition-colors ${highContrast ? 'bg-[#A2D2FF]' : 'bg-[#E8E8E8]'}`}
                role="switch"
                aria-checked={highContrast}
                aria-label="Activer le contraste élevé"
              >
                <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${highContrast ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Large Text Toggle */}
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-[#5C3D36]">Texte agrandi</p>
                <p className="text-xs text-[#9D8189]">+20% taille</p>
              </div>
              <button
                onClick={toggleLargeText}
                className={`relative w-12 h-7 rounded-full transition-colors ${largeText ? 'bg-[#CDB4DB]' : 'bg-[#E8E8E8]'}`}
                role="switch"
                aria-checked={largeText}
                aria-label="Activer le texte agrandi"
              >
                <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${largeText ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <p className="text-xs text-[#9D8189] mt-3 text-center">
              Ces options restent actives après connexion
            </p>
          </div>
        )}

        <div className="text-center max-w-md animate-fade-in">
          <img src={logoImg} alt="MamaLink Logo" className="w-32 h-32 mx-auto mb-6 object-contain" />
          
          <h1 className="text-4xl md:text-5xl font-black text-[#5C3D36] mb-4 font-serif">
            MamaLink
          </h1>
          
          <p className="text-lg text-[#9D8189] mb-8">
            Choisis ta langue / Choose your language / Elige tu idioma / Scegli la tua lingua
          </p>

          <div className="space-y-4">
            <button 
              onClick={() => handleLanguageSelect('fr')}
              className="w-full mama-card flex items-center justify-center gap-4 py-6 hover:border-[#FFB5A7] transition-all"
              data-testid="lang-select-fr"
            >
              <span className="w-10 h-10 rounded-full bg-[#FFB5A7]/20 flex items-center justify-center font-bold text-[#FFB5A7]">FR</span>
              <span className="font-medium text-[#5C3D36] text-lg">Français</span>
            </button>
            
            <button 
              onClick={() => handleLanguageSelect('en')}
              className="w-full mama-card flex items-center justify-center gap-4 py-6 hover:border-[#FFB5A7] transition-all"
              data-testid="lang-select-en"
            >
              <span className="w-10 h-10 rounded-full bg-[#A2D2FF]/20 flex items-center justify-center font-bold text-[#A2D2FF]">EN</span>
              <span className="font-medium text-[#5C3D36] text-lg">English</span>
            </button>

            <button 
              onClick={() => handleLanguageSelect('es')}
              className="w-full mama-card flex items-center justify-center gap-4 py-6 hover:border-[#FFB5A7] transition-all"
              data-testid="lang-select-es"
            >
              <span className="w-10 h-10 rounded-full bg-[#FFE5B4]/20 flex items-center justify-center font-bold text-[#E6A817]">ES</span>
              <span className="font-medium text-[#5C3D36] text-lg">Español</span>
            </button>

            <button 
              onClick={() => handleLanguageSelect('it')}
              className="w-full mama-card flex items-center justify-center gap-4 py-6 hover:border-[#FFB5A7] transition-all"
              data-testid="lang-select-it"
            >
              <span className="w-10 h-10 rounded-full bg-[#B5E48C]/20 flex items-center justify-center font-bold text-[#52B788]">IT</span>
              <span className="font-medium text-[#5C3D36] text-lg">Italiano</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FCD5CE] via-[#FDE2E4] to-[#FFF1E6] flex flex-col items-center justify-center p-6" role="main" aria-label="Page d'accueil MamaLink">
      <SkipLink />
      
      {/* Accessibility Button - Fixed position */}
      <button
        onClick={() => setShowAccessibility(!showAccessibility)}
        className="fixed top-4 right-4 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all z-50 border-2 border-[#A2D2FF]"
        aria-label="Options d'accessibilité"
        aria-expanded={showAccessibility}
        data-testid="landing-accessibility-toggle"
      >
        <Eye className="w-6 h-6 text-[#A2D2FF]" aria-hidden="true" />
      </button>

      {/* Accessibility Panel */}
      {showAccessibility && (
        <div className="fixed top-16 right-4 bg-white rounded-2xl shadow-xl p-4 z-50 w-72 border-2 border-[#A2D2FF]" role="dialog" aria-label="Options d'accessibilité">
          <h2 className="font-bold text-[#5C3D36] mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#A2D2FF]" aria-hidden="true" />
            Accessibilité
          </h2>
          
          {/* High Contrast Toggle */}
          <div className="flex items-center justify-between py-3 border-b border-[#F8EDEB]">
            <div>
              <p className="font-medium text-[#5C3D36]">Contraste élevé</p>
              <p className="text-xs text-[#9D8189]">Pour malvoyants</p>
            </div>
            <button
              onClick={toggleHighContrast}
              className={`relative w-12 h-7 rounded-full transition-colors ${highContrast ? 'bg-[#A2D2FF]' : 'bg-[#E8E8E8]'}`}
              role="switch"
              aria-checked={highContrast}
              aria-label="Activer le contraste élevé"
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${highContrast ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Large Text Toggle */}
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-[#5C3D36]">Texte agrandi</p>
              <p className="text-xs text-[#9D8189]">+20% taille</p>
            </div>
            <button
              onClick={toggleLargeText}
              className={`relative w-12 h-7 rounded-full transition-colors ${largeText ? 'bg-[#CDB4DB]' : 'bg-[#E8E8E8]'}`}
              role="switch"
              aria-checked={largeText}
              aria-label="Activer le texte agrandi"
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${largeText ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <p className="text-xs text-[#9D8189] mt-3 text-center">
            Ces options restent actives après connexion
          </p>
        </div>
      )}

      <div id="main-content" className="text-center max-w-md animate-fade-in">
        <img src={logoImg} alt="Logo MamaLink - Application de rencontre entre mamans" className="w-32 h-32 mx-auto mb-6 object-contain mix-blend-multiply" />
        
        <h1 className="text-4xl md:text-5xl font-black text-[#5C3D36] mb-4 font-serif">
          MamaLink
        </h1>
        
        <p className="text-xl text-[#5C3D36]/80 mb-2" aria-label="Bienvenue">
          {t('welcome')}
        </p>
        
        <p className="text-lg text-[#9D8189] mb-12 font-accent text-2xl">
          {t('subtitle')}
        </p>

        <nav className="space-y-4" aria-label="Navigation principale">
          <button 
            onClick={() => navigate('/login')}
            className="w-full mama-btn-primary text-lg"
            data-testid="login-btn"
            aria-label="Se connecter à mon compte"
          >
            {t('login')}
          </button>
          
          <button 
            onClick={() => navigate('/register')}
            className="w-full mama-btn-secondary text-lg"
            data-testid="register-btn"
            aria-label="Créer un nouveau compte"
          >
            {t('register')}
          </button>
        </nav>

        <div className="mt-8 flex justify-center gap-4" role="group" aria-label="Sélection de la langue">
          <LanguageSelector />
        </div>

        {/* Share MamaLink */}
        <div className="mt-6 text-center">
          <p className="text-sm text-[#9D8189] mb-2">Partage MamaLink avec tes amies !</p>
          <div className="flex justify-center">
            <SocialShare 
              title="MamaLink - L'app des mamans"
              text="Rejoins MamaLink, l'application pour connecter les mamans ! 💕 Trouve ta meilleure amie de maternité."
              hashtags={["MamaLink", "Mamans", "Maternité"]}
            />
          </div>
        </div>

        {/* Footer légal */}
        <footer className="mt-8 pt-4 border-t border-[#F8EDEB]/50" role="contentinfo">
          <nav className="flex flex-wrap justify-center gap-3 text-xs text-[#9D8189]" aria-label="Liens légaux">
            <a href="/privacy-policy" className="hover:text-[#FFB5A7] hover:underline">Politique de confidentialité</a>
            <span aria-hidden="true">•</span>
            <a href="/terms-of-service" className="hover:text-[#FFB5A7] hover:underline">CGU</a>
            <span aria-hidden="true">•</span>
            <a href="/legal-notice" className="hover:text-[#FFB5A7] hover:underline">Mentions légales</a>
            <span aria-hidden="true">•</span>
            <a href="/delete-account" className="hover:text-[#FFB5A7] hover:underline" data-testid="footer-delete-account-link">Supprimer mon compte</a>
          </nav>
          <p className="text-center text-xs text-[#C9ADA7] mt-2">© 2026 MamaLink</p>
        </footer>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#FFF1E6] to-transparent pointer-events-none" aria-hidden="true" />
    </div>
  );
};

// Language Selector
const LanguageSelector = () => {
  const { language, changeLanguage } = useLanguage();
  const languages = [
    { code: 'fr', label: 'FR', fullName: 'Français' },
    { code: 'en', label: 'EN', fullName: 'English' },
    { code: 'es', label: 'ES', fullName: 'Español' },
    { code: 'it', label: 'IT', fullName: 'Italiano' }
  ];

  return (
    <div className="flex items-center gap-1 bg-white/50 rounded-full px-3 py-2" role="radiogroup" aria-label="Sélection de la langue">
      {languages.map(lang => (
        <button 
          key={lang.code}
          onClick={() => changeLanguage(lang.code)}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
            language === lang.code ? 'bg-[#FFB5A7] text-[#5C3D36]' : 'text-[#9D8189] hover:text-[#5C3D36]'
          }`}
          data-testid={`lang-${lang.code}`}
          role="radio"
          aria-checked={language === lang.code}
          aria-label={lang.fullName}
        >
          {lang.label}
        </button>
      ))}
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const { t } = useLanguage();
  const { login, user, loading: authLoading } = useAuth();
  const { highContrast, toggleHighContrast, largeText, toggleLargeText } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [showAccessibility, setShowAccessibility] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);
    try {
      await login(email, password, rememberMe);
      toast.success('Connexion réussie !', { duration: 1500 });
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erreur de connexion';
      // Si le compte n'est pas vérifié, on propose un renvoi de l'email
      if (error.response?.status === 403 && /vérifier ton email/i.test(detail)) {
        setNeedsVerification(true);
      }
      toast.error(detail);
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Saisis ton email d\'abord 💕');
      return;
    }
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success('Email envoyé ! Vérifie ta boîte (et les spams) 💕');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setResending(false);
  };

  return (
    <div className="min-h-screen bg-[#FFF1E6] flex items-center justify-center p-6" role="main" aria-label="Page de connexion">
      <SkipLink />
      
      {/* Accessibility Button - Fixed position */}
      <button
        onClick={() => setShowAccessibility(!showAccessibility)}
        className="fixed top-4 right-4 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all z-50 border-2 border-[#A2D2FF]"
        aria-label="Options d'accessibilité"
        aria-expanded={showAccessibility}
        data-testid="accessibility-toggle"
      >
        <Eye className="w-6 h-6 text-[#A2D2FF]" aria-hidden="true" />
      </button>

      {/* Accessibility Panel */}
      {showAccessibility && (
        <div className="fixed top-16 right-4 bg-white rounded-2xl shadow-xl p-4 z-50 w-72 border-2 border-[#A2D2FF]" role="dialog" aria-label="Options d'accessibilité">
          <h2 className="font-bold text-[#5C3D36] mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#A2D2FF]" aria-hidden="true" />
            Accessibilité
          </h2>
          
          {/* High Contrast Toggle */}
          <div className="flex items-center justify-between py-3 border-b border-[#F8EDEB]">
            <div>
              <p className="font-medium text-[#5C3D36]">Contraste élevé</p>
              <p className="text-xs text-[#9D8189]">Pour malvoyants</p>
            </div>
            <button
              onClick={toggleHighContrast}
              className={`relative w-12 h-7 rounded-full transition-colors ${highContrast ? 'bg-[#A2D2FF]' : 'bg-[#E8E8E8]'}`}
              role="switch"
              aria-checked={highContrast}
              aria-label="Activer le contraste élevé"
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${highContrast ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Large Text Toggle */}
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-[#5C3D36]">Texte agrandi</p>
              <p className="text-xs text-[#9D8189]">+20% taille</p>
            </div>
            <button
              onClick={toggleLargeText}
              className={`relative w-12 h-7 rounded-full transition-colors ${largeText ? 'bg-[#CDB4DB]' : 'bg-[#E8E8E8]'}`}
              role="switch"
              aria-checked={largeText}
              aria-label="Activer le texte agrandi"
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${largeText ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <p className="text-xs text-[#9D8189] mt-3 text-center">
            Ces options restent actives après connexion
          </p>
        </div>
      )}

      <div id="main-content" className="w-full max-w-md">
        <button onClick={() => navigate('/')} className="mb-8 flex items-center text-[#9D8189] hover:text-[#5C3D36] transition-colors" aria-label="Retourner à l'accueil">
          <ArrowLeft className="w-5 h-5 mr-2" aria-hidden="true" />
          Retour
        </button>

        <div className="mama-card animate-fade-in">
          <div className="text-center mb-8">
            <Heart className="w-12 h-12 mx-auto text-[#FFB5A7] mb-4" aria-hidden="true" />
            <h1 className="text-2xl font-bold text-[#5C3D36] font-serif">{t('login')}</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" aria-label="Formulaire de connexion">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-[#9D8189] mb-2">{t('email')}</label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mama-input"
                placeholder="maman@example.com"
                required
                autoComplete="email"
                aria-required="true"
                data-testid="login-email"
              />
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-[#9D8189] mb-2">{t('password')}</label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full mama-input pr-12"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  aria-required="true"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#9D8189] hover:text-[#5C3D36]"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  data-testid="toggle-password-login"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" aria-hidden="true" /> : <Eye className="w-5 h-5" aria-hidden="true" />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password Row */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setRememberMe(!rememberMe)}
                  className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                    rememberMe 
                      ? 'bg-[#FFB5A7] border-[#FFB5A7]' 
                      : 'border-[#C9ADA7] bg-white'
                  }`}
                  role="checkbox"
                  aria-checked={rememberMe}
                  aria-label="Se souvenir de moi"
                  data-testid="remember-me-checkbox"
                >
                  {rememberMe && <Check className="w-4 h-4 text-white" aria-hidden="true" />}
                </button>
                <span 
                  className="text-sm text-[#5C3D36] cursor-pointer select-none"
                  onClick={() => setRememberMe(!rememberMe)}
                >
                  Rester connectée
                </span>
              </div>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-[#FFB5A7] hover:text-[#FFA090] hover:underline"
                data-testid="forgot-password-btn"
              >
                Mot de passe oublié ?
              </button>
            </div>

            <button 
              type="submit" 
              className="w-full mama-btn-primary"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? t('loading') : t('login')}
            </button>
          </form>

          {needsVerification && (
            <div className="mt-4 p-4 rounded-2xl bg-[#FFF1E6] border border-[#FFB5A7]" data-testid="needs-verification-banner">
              <p className="text-sm text-[#5C3D36] mb-3">
                ✉️ Ton email n'est pas encore vérifié. Vérifie ta boîte mail (et tes spams).
              </p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resending}
                className="w-full px-4 py-2 bg-gradient-to-r from-[#FFB5A7] to-[#FCD5CE] text-white font-semibold rounded-full shadow hover:shadow-md transition-all disabled:opacity-60"
                data-testid="resend-verification-btn"
              >
                {resending ? 'Envoi...' : 'Renvoyer l\'email de vérification 💕'}
              </button>
            </div>
          )}

          <p className="text-center mt-6 text-[#9D8189]">
            {t('noAccount')}{' '}
            <button onClick={() => navigate('/register')} className="text-[#FFB5A7] font-semibold hover:underline">
              {t('register')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const { t, language } = useLanguage();
  const { register, user, loading: authLoading } = useAuth();
  const { highContrast, toggleHighContrast, largeText, toggleLargeText } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [firstNameStatus, setFirstNameStatus] = useState({ checking: false, available: null, suggestions: [], reason: null });
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  const [referralBonus, setReferralBonus] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptCGU, setAcceptCGU] = useState(false);
  const [acceptMaternityData, setAcceptMaternityData] = useState(false);
  const [showAccessibility, setShowAccessibility] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Pre-fill referral code from URL (?code=MAMA-XXXX)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('code');
    if (urlCode) {
      const cleanCode = urlCode.toUpperCase().trim();
      setReferralCode(cleanCode);
      validateReferralCode(cleanCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(null);
      setReferralBonus('');
      return;
    }
    try {
      const res = await axios.get(`${API}/referral/validate/${code}`);
      if (res.data.valid) {
        setReferralValid(true);
        setReferralBonus(`Code de ${res.data.referrer_name} - ${res.data.bonus}`);
      } else {
        setReferralValid(false);
        setReferralBonus('');
      }
    } catch (err) {
      setReferralValid(false);
      setReferralBonus('');
    }
  };

  // Vérification temps réel du prénom (debounce 400ms)
  useEffect(() => {
    const fn = firstName.trim();
    if (fn.length < 2) {
      setFirstNameStatus({ checking: false, available: null, suggestions: [], reason: fn.length === 0 ? null : 'too_short' });
      return;
    }
    setFirstNameStatus(prev => ({ ...prev, checking: true }));
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/auth/check-firstname`, { params: { first_name: fn } });
        setFirstNameStatus({
          checking: false,
          available: !!res.data?.available,
          suggestions: res.data?.suggestions || [],
          reason: res.data?.reason || null,
        });
      } catch (e) {
        setFirstNameStatus({ checking: false, available: null, suggestions: [], reason: null });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [firstName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptCGU) {
      toast.error('Veuillez accepter les Conditions Générales d\'Utilisation');
      return;
    }
    if (!acceptMaternityData) {
      toast.error('Veuillez accepter le traitement de vos données de maternité pour utiliser le service');
      return;
    }
    if (firstNameStatus.available === false) {
      toast.error('Ce prénom est déjà pris. Choisis-en un autre 💕');
      return;
    }
    setLoading(true);
    try {
      await register(email, password, firstName, language, referralCode.trim() || null);
      // ✉️ Vérification email obligatoire : redirige vers l'écran "Vérifie ta boîte mail"
      navigate(`/check-email?email=${encodeURIComponent(email)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FFF1E6] flex items-center justify-center p-6" role="main" aria-label="Page d'inscription">
      <SkipLink />
      
      {/* Accessibility Button - Fixed position */}
      <button
        onClick={() => setShowAccessibility(!showAccessibility)}
        className="fixed top-4 right-4 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all z-50 border-2 border-[#A2D2FF]"
        aria-label="Options d'accessibilité"
        aria-expanded={showAccessibility}
        data-testid="accessibility-toggle-register"
      >
        <Eye className="w-6 h-6 text-[#A2D2FF]" aria-hidden="true" />
      </button>

      {/* Accessibility Panel */}
      {showAccessibility && (
        <div className="fixed top-16 right-4 bg-white rounded-2xl shadow-xl p-4 z-50 w-72 border-2 border-[#A2D2FF]" role="dialog" aria-label="Options d'accessibilité">
          <h2 className="font-bold text-[#5C3D36] mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#A2D2FF]" aria-hidden="true" />
            Accessibilité
          </h2>
          
          <div className="flex items-center justify-between py-3 border-b border-[#F8EDEB]">
            <div>
              <p className="font-medium text-[#5C3D36]">Contraste élevé</p>
              <p className="text-xs text-[#9D8189]">Pour malvoyants</p>
            </div>
            <button
              onClick={toggleHighContrast}
              className={`relative w-12 h-7 rounded-full transition-colors ${highContrast ? 'bg-[#A2D2FF]' : 'bg-[#E8E8E8]'}`}
              role="switch"
              aria-checked={highContrast}
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${highContrast ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <p className="font-medium text-[#5C3D36]">Texte agrandi</p>
              <p className="text-xs text-[#9D8189]">+20% taille</p>
            </div>
            <button
              onClick={toggleLargeText}
              className={`relative w-12 h-7 rounded-full transition-colors ${largeText ? 'bg-[#CDB4DB]' : 'bg-[#E8E8E8]'}`}
              role="switch"
              aria-checked={largeText}
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${largeText ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
      )}

      <div id="main-content" className="w-full max-w-md">
        <button onClick={() => navigate('/')} className="mb-8 flex items-center text-[#9D8189] hover:text-[#5C3D36] transition-colors" aria-label="Retourner à l'accueil">
          <ArrowLeft className="w-5 h-5 mr-2" aria-hidden="true" />
          Retour
        </button>

        <div className="mama-card animate-fade-in">
          <div className="text-center mb-8">
            <Heart className="w-12 h-12 mx-auto text-[#FFB5A7] mb-4" aria-hidden="true" />
            <h1 className="text-2xl font-bold text-[#5C3D36] font-serif">{t('register')}</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" aria-label="Formulaire d'inscription">
            <div>
              <label htmlFor="register-firstname" className="block text-sm font-medium text-[#9D8189] mb-2">{t('firstName')}</label>
              <input
                id="register-firstname"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={`w-full mama-input ${firstNameStatus.available === false ? 'border-red-300' : firstNameStatus.available === true ? 'border-green-300' : ''}`}
                placeholder="Marie"
                required
                aria-required="true"
                data-testid="register-firstname"
              />
              {/* Indicateur de disponibilité du prénom */}
              {firstName.trim().length >= 2 && (
                <div className="mt-2 text-xs" data-testid="firstname-status">
                  {firstNameStatus.checking && (
                    <span className="text-[#9D8189] inline-flex items-center gap-1">
                      <span className="w-3 h-3 border-2 border-[#FFB5A7] border-t-transparent rounded-full animate-spin" />
                      Vérification…
                    </span>
                  )}
                  {!firstNameStatus.checking && firstNameStatus.available === true && (
                    <span className="text-green-600 font-medium">✓ Ce prénom est disponible !</span>
                  )}
                  {!firstNameStatus.checking && firstNameStatus.available === false && firstNameStatus.reason === 'taken' && (
                    <div>
                      <p className="text-red-500 font-medium">✗ Ce prénom est déjà pris par une autre maman</p>
                      {firstNameStatus.suggestions.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <span className="text-[#9D8189] self-center">Suggestions :</span>
                          {firstNameStatus.suggestions.map(s => (
                            <button
                              key={s}
                              type="button"
                              onClick={() => setFirstName(s)}
                              className="px-2.5 py-1 rounded-full bg-[#FFF1E6] text-[#5C3D36] hover:bg-[#FCD5CE] transition-colors text-xs font-medium"
                              data-testid={`firstname-suggestion-${s}`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {!firstNameStatus.checking && firstNameStatus.reason === 'too_short' && (
                    <span className="text-red-500">✗ Au moins 2 caractères</span>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9D8189] mb-2">{t('email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mama-input"
                placeholder="maman@example.com"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9D8189] mb-2">{t('password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full mama-input pr-12"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#9D8189] hover:text-[#5C3D36]"
                  data-testid="toggle-password-register"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Referral Code (optional) */}
            <div>
              <label className="block text-sm font-medium text-[#9D8189] mb-2">
                Code parrain (optionnel)
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => {
                    const code = e.target.value.toUpperCase();
                    setReferralCode(code);
                    validateReferralCode(code);
                  }}
                  className={`w-full mama-input pr-10 ${
                    referralValid === true ? 'border-green-400 ring-1 ring-green-400' :
                    referralValid === false ? 'border-red-300' : ''
                  }`}
                  placeholder="MARIE-A7X9"
                  data-testid="register-referral-code"
                />
                {referralValid === true && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                    ✓
                  </div>
                )}
              </div>
              {referralBonus && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  🎁 {referralBonus}
                </p>
              )}
              {referralValid === false && referralCode.length >= 4 && (
                <p className="text-xs text-red-500 mt-1">Code invalide</p>
              )}
            </div>

            {/* CGU Checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="acceptCGU"
                checked={acceptCGU}
                onChange={(e) => setAcceptCGU(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-[#C9ADA7] text-[#FFB5A7] focus:ring-[#FFB5A7]"
                data-testid="register-accept-cgu"
              />
              <label htmlFor="acceptCGU" className="text-sm text-[#5C3D36]">
                J'ai lu et j'accepte les{' '}
                <button type="button" onClick={() => window.open('/dashboard/terms-of-service', '_blank')} className="text-[#FFB5A7] underline">
                  Conditions Générales d'Utilisation
                </button>
                {' '}et la{' '}
                <button type="button" onClick={() => window.open('/dashboard/privacy-policy', '_blank')} className="text-[#FFB5A7] underline">
                  Politique de Confidentialité
                </button>
              </label>
            </div>

            {/* Maternity Data Consent Checkbox (RGPD - données sensibles) */}
            <div className="bg-[#FDE2E4] rounded-xl p-3">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="acceptMaternityData"
                  checked={acceptMaternityData}
                  onChange={(e) => setAcceptMaternityData(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-[#C9ADA7] text-[#FFB5A7] focus:ring-[#FFB5A7]"
                  data-testid="register-accept-maternity-data"
                />
                <label htmlFor="acceptMaternityData" className="text-sm text-[#5C3D36]">
                  <strong>Consentement données de maternité :</strong><br />
                  J'accepte le traitement de mes données relatives à ma maternité (grossesse, enfants) pour bénéficier du service de matching avec d'autres mamans.
                  <span className="block text-xs text-[#9D8189] mt-1">
                    Ces données sont considérées comme sensibles (RGPD). Vous pouvez retirer ce consentement à tout moment dans les paramètres.
                  </span>
                </label>
              </div>
            </div>

            <button 
              type="submit" 
              className="w-full mama-btn-primary"
              disabled={loading || !acceptCGU || !acceptMaternityData}
              data-testid="register-submit"
            >
              {loading ? t('loading') : t('register')}
            </button>
          </form>

          <p className="text-center mt-6 text-[#9D8189]">
            {t('alreadyHaveAccount')}{' '}
            <button onClick={() => navigate('/login')} className="text-[#FFB5A7] font-semibold hover:underline">
              {t('login')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// Forgot Password Page
const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Email envoyé ! Vérifie ta boîte de réception.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FFF1E6] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <button onClick={() => navigate('/login')} className="mb-8 flex items-center text-[#9D8189] hover:text-[#5C3D36] transition-colors">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour à la connexion
        </button>

        <div className="mama-card animate-fade-in">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto bg-gradient-to-r from-[#FFB5A7] to-[#FCD5CE] rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#5C3D36] font-serif">Mot de passe oublié ?</h1>
            <p className="text-sm text-[#9D8189] mt-2">
              Pas de panique ! Entre ton email et on t'envoie un lien pour le réinitialiser.
            </p>
          </div>

          {sent ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto bg-[#D8F3DC] rounded-full flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-[#52B788]" />
              </div>
              <h2 className="text-lg font-semibold text-[#5C3D36] mb-2">Email envoyé !</h2>
              <p className="text-sm text-[#9D8189] mb-4">
                Si cette adresse est associée à un compte, tu recevras un email avec les instructions.
              </p>
              <p className="text-xs text-[#C9ADA7] mb-6">
                Pense à vérifier tes spams si tu ne le vois pas.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="mama-btn-primary w-full"
              >
                Retour à la connexion
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#9D8189] mb-2">Ton adresse email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full mama-input"
                  placeholder="maman@example.com"
                  required
                  data-testid="forgot-email"
                />
              </div>

              <button 
                type="submit" 
                className="w-full mama-btn-primary"
                disabled={loading}
                data-testid="forgot-submit"
              >
                {loading ? 'Envoi en cours...' : 'Envoyer le lien'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Reset Password Page
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const emailFromUrl = searchParams.get('email') || '';
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResend = async () => {
    const target = email || emailFromUrl;
    if (!target) {
      navigate('/forgot-password');
      return;
    }
    setResending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: target });
      setResent(true);
      toast.success(`Un nouveau lien vient d'être envoyé à ${target} 💕`);
    } catch (e) {
      toast.error('Erreur lors de l\'envoi du nouveau lien.');
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setValidating(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
      }
      setValidating(false);
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Mot de passe modifié avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
    setLoading(false);
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-[#FFF1E6] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto text-[#FFB5A7] animate-spin" />
          <p className="mt-4 text-[#9D8189]">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    const knownEmail = email || emailFromUrl;
    return (
      <div className="min-h-screen bg-[#FFF1E6] flex items-center justify-center p-6">
        <div className="w-full max-w-md mama-card text-center">
          <div className="w-16 h-16 mx-auto bg-[#FDE2E4] rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-[#FF6B6B]" />
          </div>
          <h1 className="text-xl font-bold text-[#5C3D36] mb-2">Lien invalide ou expiré</h1>
          <p className="text-sm text-[#9D8189] mb-6">
            Ce lien de réinitialisation n'est plus valide. Il a peut-être expiré (validité 24h) ou a déjà été utilisé.
          </p>

          {resent ? (
            <div className="p-4 bg-[#E8F5E9] rounded-2xl mb-4 text-sm text-[#2D5A3D]" data-testid="resend-success-banner">
              ✅ Nouveau lien envoyé à <strong>{knownEmail}</strong> ! Vérifie ta boîte mail (et les spams).
            </div>
          ) : knownEmail ? (
            <>
              <button
                onClick={handleResend}
                disabled={resending}
                className="mama-btn-primary w-full mb-3 disabled:opacity-60"
                data-testid="resend-reset-link-btn"
              >
                {resending ? 'Envoi en cours…' : `📧 Renvoyer un lien à ${knownEmail}`}
              </button>
              <button
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-[#9D8189] underline"
              >
                Utiliser un autre email
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate('/forgot-password')}
              className="mama-btn-primary w-full"
            >
              Demander un nouveau lien
            </button>
          )}
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#FFF1E6] flex items-center justify-center p-6">
        <div className="w-full max-w-md mama-card text-center">
          <div className="w-16 h-16 mx-auto bg-[#D8F3DC] rounded-full flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-[#52B788]" />
          </div>
          <h1 className="text-xl font-bold text-[#5C3D36] mb-2">Mot de passe modifié !</h1>
          <p className="text-sm text-[#9D8189] mb-6">
            Tu peux maintenant te connecter avec ton nouveau mot de passe.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="mama-btn-primary w-full"
          >
            Me connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF1E6] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mama-card animate-fade-in">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto bg-gradient-to-r from-[#FFB5A7] to-[#FCD5CE] rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#5C3D36] font-serif">Nouveau mot de passe</h1>
            <p className="text-sm text-[#9D8189] mt-2">
              Choisis un nouveau mot de passe pour <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#9D8189] mb-2">Nouveau mot de passe</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full mama-input pr-12"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="reset-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#9D8189] hover:text-[#5C3D36]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9D8189] mb-2">Confirmer le mot de passe</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full mama-input"
                placeholder="••••••••"
                required
                data-testid="reset-confirm-password"
              />
            </div>

            <button 
              type="submit" 
              className="w-full mama-btn-primary"
              disabled={loading}
              data-testid="reset-submit"
            >
              {loading ? 'Modification...' : 'Modifier mon mot de passe'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF1E6] flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-16 h-16 mx-auto text-[#FFB5A7] animate-pulse-soft" />
          <p className="mt-4 text-[#9D8189]">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  // Redirect to charter page if not accepted (except if already on charter page)
  if (!user.charter_accepted && !location.pathname.includes('/charter')) {
    return <Navigate to="/dashboard/charter" state={{ firstTime: true }} replace />;
  }
  
  return children;
};

// Dashboard Layout
const DashboardLayout = ({ children }) => {
  const { t } = useLanguage();
  const { user, logout, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  // 🔴 Élodie #791 — pastille rouge sur "Aide & Support" et "Messages" quand l'admin a répondu
  const [supportUnread, setSupportUnread] = useState(0);
  const [messagesUnread, setMessagesUnread] = useState(0); // 🔴 Élodie #1084 — badge Messages non lus
  const [liveChatUnread, setLiveChatUnread] = useState(0); // 🟢 Élodie #1094 — point vert Chat en ligne
  // 🎁 Élodie #1095 — Pack Bienvenue MamaLink (compte à rebours dans le header)
  const [starterPack, setStarterPack] = useState(null); // { is_active, days_left, hours_left, seen_welcome, expires_at }
  const prevSupportUnreadRef = useRef(0);
  // 🔔 Élodie #792 — préférence "silencer le son" persistée en localStorage
  const [supportSoundMuted, setSupportSoundMuted] = useState(() => localStorage.getItem('mamalink_support_sound_muted') === '1');
  const [showOnboarding, setShowOnboarding] = useState(false);

  // 🎨 Emoji perso partagé (bébé + maman) — lu depuis localStorage pour synchro avec le personalizer du dashboard
  const emojiPrefsKey = user?.id ? `mamalink_emoji_prefs_${user.id}` : 'mamalink_emoji_prefs';
  const [emojiPrefs, setEmojiPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(emojiPrefsKey);
      return raw ? JSON.parse(raw) : { skin: '', hair: '' };
    } catch { return { skin: '', hair: '' }; }
  });
  // Re-sync quand localStorage change (event storage ou custom)
  useEffect(() => {
    const sync = () => {
      try {
        const raw = localStorage.getItem(emojiPrefsKey);
        if (raw) setEmojiPrefs(JSON.parse(raw));
      } catch { /* ignore */ }
    };
    window.addEventListener('storage', sync);
    window.addEventListener('mamalink-emoji-prefs-updated', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('mamalink-emoji-prefs-updated', sync);
    };
  }, [emojiPrefsKey]);

  // Check if user needs onboarding (first login)
  useEffect(() => {
    if (user && !user.has_completed_onboarding) {
      // Small delay to let the dashboard render first
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Update local user state
    if (updateUser) {
      updateUser({ has_completed_onboarding: true });
    }
  };

  // Handle mobile menu with browser history
  const openMobileMenu = useCallback(() => {
    setMobileMenuOpen(true);
    // Push a state to history so back button closes the menu
    window.history.pushState({ menuOpen: true }, '', location.pathname);
  }, [location.pathname]);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Handle back button to close menu
  useEffect(() => {
    const handlePopState = (event) => {
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
        // Prevent default navigation - menu closing is the action
        event.preventDefault();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [mobileMenuOpen]);

  // Navigate from menu and close it
  const handleMenuNavigation = useCallback((path) => {
    closeMobileMenu();
    // Small delay to allow menu to close smoothly
    setTimeout(() => {
      navigate(path);
    }, 100);
  }, [navigate, closeMobileMenu]);

  // Fetch unread notifications count
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Count notifications that are NOT read (check both fields)
      const unread = response.data.filter(n => !(n.read === true || n.is_read === true)).length;
      setUnreadNotifications(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [token]);

  // Mark all notifications as read and refresh count
  const markAllAsRead = useCallback(async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadNotifications(0);
      return true;
    } catch (error) {
      console.error('Error marking all as read:', error);
      return false;
    }
  }, [token]);

  useEffect(() => {
    fetchUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // 💌 Élodie #791 — Poll du nombre de réponses admin non lues (pastille rouge sidebar)
  const fetchSupportUnread = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/support/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const next = Number(r.data?.unread) || 0;
      // 🔔 Élodie #792 — son doux quand une nouvelle réponse admin arrive
      const prev = prevSupportUnreadRef.current;
      if (next > prev && prev >= 0) {
        // Nouveau(x) message(s) : joue le son (sauf si mute)
        if (!(localStorage.getItem('mamalink_support_sound_muted') === '1')) {
          try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (AC) {
              const ctx = new AC();
              // Résume si suspended (autoplay policy)
              if (ctx.state === 'suspended') { ctx.resume().catch(() => {}); }
              const now = ctx.currentTime;
              // 2 notes descendantes très douces (E5 → C5), fade in/out — style WhatsApp doux
              const notes = [{ f: 659.25, t: 0 }, { f: 523.25, t: 0.14 }];
              notes.forEach(({ f, t }) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = f;
                gain.gain.setValueAtTime(0, now + t);
                gain.gain.linearRampToValueAtTime(0.09, now + t + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.28);
                osc.connect(gain).connect(ctx.destination);
                osc.start(now + t);
                osc.stop(now + t + 0.32);
              });
              setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 800);
            }
          } catch { /* silent */ }
        }
        // Toast bienveillant
        toast.success('💕 L\'équipe MamaLink t\'a répondu', {
          description: 'Ouvre le chat pour lire ta réponse 🌸',
          duration: 6000,
        });
      }
      prevSupportUnreadRef.current = next;
      setSupportUnread(next);
    } catch { /* silent */ }
  }, [token]);
  useEffect(() => {
    fetchSupportUnread();
    const iv = setInterval(fetchSupportUnread, 30000);
    return () => clearInterval(iv);
  }, [fetchSupportUnread]);
  // Refresh instantané quand la maman quitte /dashboard/support (elle vient de lire)
  useEffect(() => {
    if (location.pathname !== '/dashboard/support') {
      fetchSupportUnread();
    }
  }, [location.pathname, fetchSupportUnread]);

  // 🔴 Élodie #1084 — Poll du nombre de messages privés non lus (badge sidebar Messages)
  const fetchMessagesUnread = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessagesUnread(Number(r.data?.unread_count) || 0);
    } catch { /* silent */ }
  }, [token]);
  useEffect(() => {
    fetchMessagesUnread();
    const iv = setInterval(fetchMessagesUnread, 30000);
    return () => clearInterval(iv);
  }, [fetchMessagesUnread]);
  // Refresh instantané quand la maman ENTRE sur une page de messages (elle vient de lire) ou en sort
  useEffect(() => {
    const isOnMessages = location.pathname.startsWith('/dashboard/messages');
    if (isOnMessages) {
      // Elle ouvre les messages : les compteurs vont se remettre à 0 côté back après read
      // On refetch dans 2s le temps que le backend traite les mark-as-read
      const t1 = setTimeout(fetchMessagesUnread, 2000);
      return () => clearTimeout(t1);
    }
    fetchMessagesUnread();
  }, [location.pathname, fetchMessagesUnread]);

  // 🟢 Élodie #1094 — Poll du nombre de messages non lus dans le Chat en ligne (community chat)
  const fetchLiveChatUnread = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/community-chat/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiveChatUnread(Number(r.data?.unread_count) || 0);
    } catch { /* silent */ }
  }, [token]);
  useEffect(() => {
    fetchLiveChatUnread();
    const iv = setInterval(fetchLiveChatUnread, 30000);
    return () => clearInterval(iv);
  }, [fetchLiveChatUnread]);
  // 🎁 Élodie #1095 — Fetch Pack Bienvenue status
  const fetchStarterPack = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/starter-pack/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStarterPack(r.data || null);
    } catch { /* silent */ }
  }, [token]);
  useEffect(() => {
    fetchStarterPack();
    const iv = setInterval(fetchStarterPack, 60000);
    return () => clearInterval(iv);
  }, [fetchStarterPack]);
  // Refresh au changement de route (au cas où on vient de fermer le modal)
  useEffect(() => { fetchStarterPack(); }, [location.pathname, fetchStarterPack]);

  // Quand la maman visite le Chat en ligne → on marque comme lu + refetch immédiat
  useEffect(() => {
    const isOnLiveChat = location.pathname.startsWith('/dashboard/live-chat') || location.pathname.startsWith('/dashboard/community-chat');
    if (isOnLiveChat && token) {
      axios.post(`${BACKEND_URL}/api/community-chat/mark-seen`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => setLiveChatUnread(0)).catch(() => {});
    } else {
      fetchLiveChatUnread();
    }
  }, [location.pathname, token, fetchLiveChatUnread]);

  // Dégradé arc-en-ciel du haut vers le bas : Rose → Violet → Bleu → Vert → Jaune → Orange → Corail
  // 🌸 Baby Planner & Mom Planner tout en haut (juste après Accueil) — couleurs alignées sur les tuiles de la page d'accueil
  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Accueil', gradient: 'from-[#FFB5A7] via-[#FFC5BA] to-[#FCD5CE]' },           // 1. Rose saumon
    { path: '/dashboard/baby-planner', icon: Baby, emoji: 'baby', label: 'Baby Planner ✨', gradient: 'from-[#FFB5A7] via-[#FFC8B8] to-[#FCD5CE]' }, // 2. Rose corail cocooning (aligné tuile home)
    { path: '/dashboard/mom-planner', icon: Sparkles, emoji: 'mom', label: 'Mom Planner ✨', gradient: 'from-[#E0BBE4] via-[#E8CCE9] to-[#F0D5F5]' }, // 3. Mauve maman (aligné tuile home)
    { path: '/dashboard/my-match', icon: 'mamalink-logo', label: 'Mon Match', gradient: 'from-[#FFC8DD] via-[#FFD4E5] to-[#FFDEEB]' }, // 4. Rose bonbon — logo MamaLink blanc (aligné tuile home)
    { path: '/dashboard/messages', icon: MessageCircle, label: t('messages'), gradient: 'from-[#FFB6C1] via-[#FFC8D0] to-[#FFC8D3]' }, // 5. Rose blush
    { path: '/dashboard/live-chat', icon: MessageCircle, label: 'Chat en ligne', gradient: 'from-[#E0BBE4] via-[#E8CCE9] to-[#F0D5F5]' }, // 6. Mauve
    { path: '/dashboard/forum', icon: MessageCircle, label: t('forum'), gradient: 'from-[#CDB4DB] via-[#D8C4E3] to-[#E2D1F9]' }, // 7. Violet lavande
    { path: '/dashboard/tips-advice', icon: Sparkles, label: 'Astuces & Conseils', gradient: 'from-[#DDA0DD] via-[#E4B4E4] to-[#EBC8EB]' }, // 8. Prune
    { path: '/dashboard/premium', icon: Crown, label: 'Abonnement ✨', gradient: 'from-[#A2D2FF] via-[#B5DBFF] to-[#BDE0FE]' }, // 9. Bleu ciel
    { path: '/dashboard/bons-plans', icon: Tag, label: 'Bons Plans', gradient: 'from-[#90E0EF] via-[#A8E8F4] to-[#CAF0F8]' }, // 8. Bleu turquoise
    { path: '/dashboard/ateliers', icon: Video, label: t('ateliers'), gradient: 'from-[#87CEEB] via-[#9DD7EF] to-[#B0E0E6]' }, // 9. Bleu poudre
    { path: '/dashboard/events', icon: Calendar, label: 'Événements', gradient: 'from-[#98D8C8] via-[#ACE0D4] to-[#B8E8DC]' }, // 10. Turquoise menthe
    { path: '/dashboard/service-requests', icon: Users, label: "J'ai besoin de", gradient: 'from-[#A8E6CF] via-[#BBE9D8] to-[#DCEDC1]' }, // 11. Vert menthe
    { path: '/dashboard/nearby', icon: MapPin, label: 'Autour de moi', gradient: 'from-[#B5E48C] via-[#C6EBA4] to-[#D8F3DC]' }, // 12. Vert pomme
    { path: '/dashboard/testimonies', icon: MessageSquare, label: 'Témoignages', gradient: 'from-[#C7F9CC] via-[#D5FAD9] to-[#E8FCE8]' }, // 13. Vert lime
    { path: '/dashboard/badges', icon: Award, label: 'Badges', gradient: 'from-[#FFE066] via-[#FFEA8A] to-[#FFF3B0]' }, // 14. Jaune
    { path: '/dashboard/challenges', icon: Star, label: 'Super-Maman', gradient: 'from-[#FFD700] via-[#FFDF33] to-[#FFE566]' }, // 15. Jaune doré
    { path: '/dashboard/quiz', icon: Brain, label: 'Quiz du Jour', gradient: 'from-[#FFCB69] via-[#FFD68A] to-[#FFE4A0]' }, // 16. Jaune miel
    { path: '/dashboard/favorites', icon: Bookmark, label: 'Mes Favoris', gradient: 'from-[#FFB5A7] via-[#FFC5BA] to-[#FCD5CE]' }, // 17. Rose saumon (reprend Accueil)
    { path: '/dashboard/referral', icon: Gift, label: 'Parrainage', gradient: 'from-[#FFC8DD] via-[#FFD4E5] to-[#FFDEEB]' },           // 18. Rose bonbon
    { path: '/dashboard/urgences', icon: Phone, label: t('urgences'), gradient: 'from-[#FFB6C1] via-[#FFC8D0] to-[#FFC8D3]' },        // 19. Rose blush
    { path: '/dashboard/help', icon: HelpCircle, label: 'Aide & Support', gradient: 'from-[#E0BBE4] via-[#E8CCE9] to-[#F0D5F5]' },    // 20. Reprend Chat en ligne - Mauve
    { path: '/dashboard/settings', icon: Settings, label: 'Paramètres', gradient: 'from-[#CDB4DB] via-[#D8C4E3] to-[#E2D1F9]' },      // 21. Reprend Forum - Violet lavande
    { path: '/dashboard/charter', icon: Shield, label: 'Charte MamaLink', gradient: 'from-[#DDA0DD] via-[#E4B4E4] to-[#EBC8EB]' },    // 22. Reprend Astuces & Conseils - Prune
    { path: '/dashboard/reviews', icon: Star, label: 'Avis', gradient: 'from-[#A2D2FF] via-[#B5DBFF] to-[#BDE0FE]' },                 // 23. Reprend Bons Plans - Bleu ciel
    { path: '/dashboard/share', icon: 'logo', label: 'Partager', gradient: 'from-white via-white to-white' },             // 24. Fond blanc
  ];

  // Items admin uniquement (visibles seulement pour les emails admin)
  const ADMIN_EMAILS = ['admin@mamalink.com', 'mamalink2026@gmail.com'];
  const isAdminUser = user && ADMIN_EMAILS.includes(user.email);
  if (isAdminUser) {
    navItems.push({
      path: '/dashboard/admin',
      icon: Crown,
      label: 'Admin Dashboard',
      gradient: 'from-[#FFD700] via-[#FFE066] to-[#FFF3B0]'
    });
  }

  return (
    <div className="min-h-screen bg-[#FFF1E6] overflow-x-hidden">
      <SkipLink />
      {/* Mobile Header */}
      <header
        className={`lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-[#F8EDEB] ${
          /^\/dashboard\/(messages\/|support$|community$|live-chat$|media-gallery)/.test(location.pathname)
            ? 'hidden'
            : ''
        }`}
        role="banner"
      >
        <div className="flex items-center justify-between p-4">
          <button 
            type="button"
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-0 p-0 m-0" 
            onClick={() => navigate('/dashboard')}
            data-testid="header-logo-link"
            aria-label="Aller à l'accueil"
          >
            <img src={logoImg} alt="Logo MamaLink" className="w-10 h-10 object-contain pointer-events-none" />
            <span className="font-bold text-[#5C3D36] font-serif pointer-events-none">MamaLink</span>
          </button>
          <div className="flex items-center gap-1" role="group" aria-label="Actions rapides">
            <button 
              onClick={() => navigate('/dashboard/my-match')}
              className="p-2 rounded-full hover:bg-[#F8EDEB] relative"
              data-testid="mobile-match-btn"
              aria-label="Mon match parfait"
            >
              <img src={logoImg} alt="Mon match" className="w-6 h-6 object-contain pointer-events-none" />
            </button>
            <button
              onClick={() => navigate('/dashboard/favorites')}
              className="p-2 rounded-full hover:bg-[#F8EDEB] relative"
              data-testid="mobile-favorites-btn"
              aria-label="Mes favoris"
            >
              <Bookmark className="w-5 h-5 text-[#FFCB69]" fill="#FFCB69" aria-hidden="true" />
            </button>
            <button 
              onClick={() => navigate('/dashboard/notifications')}
              className="p-2 rounded-full hover:bg-[#F8EDEB] relative"
              data-testid="mobile-notifications-btn"
              aria-label={`Notifications${unreadNotifications > 0 ? ` (${unreadNotifications} non lues)` : ''}`}
            >
              <Bell className="w-5 h-5 text-[#5C3D36]" aria-hidden="true" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FFB5A7] text-white text-xs rounded-full flex items-center justify-center font-bold" aria-hidden="true">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </button>
            <button 
              onClick={() => navigate('/dashboard/profile-settings')}
              className="p-2 rounded-full hover:bg-[#F8EDEB]"
              data-testid="mobile-profile-btn"
              aria-label="Mon profil"
            >
              <User className="w-5 h-5 text-[#5C3D36]" aria-hidden="true" />
            </button>
            <button 
              onClick={() => mobileMenuOpen ? closeMobileMenu() : openMobileMenu()}
              className="relative p-2 rounded-full hover:bg-[#F8EDEB]"
              data-testid="mobile-menu-toggle"
              aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-[#5C3D36]" aria-hidden="true" /> : <Menu className="w-6 h-6 text-[#5C3D36]" aria-hidden="true" />}
              {/* 🟢 Élodie #1093/#1094 — Petit point vert pulsant sur l'icône menu quand messages OU chat en ligne non lus */}
              {!mobileMenuOpen && (messagesUnread > 0 || liveChatUnread > 0) && (
                <span
                  className="absolute top-1 right-1 flex items-center justify-center"
                  aria-hidden="true"
                  data-testid="mobile-menu-unread-dot"
                >
                  <span className="absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-white shadow-sm" />
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" onClick={closeMobileMenu} role="dialog" aria-modal="true" aria-labelledby="mobile-menu-title">
          <div id="mobile-navigation" className="absolute top-16 right-0 w-72 bg-white rounded-l-3xl shadow-xl p-4 max-h-[calc(100vh-5rem)] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 id="mobile-menu-title" className="sr-only">Menu de navigation</h2>
            <nav className="space-y-1" aria-label="Navigation principale">
              {navItems.map(item => (
                <button
                  key={item.path}
                  onClick={() => handleMenuNavigation(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl transition-all ${
                    location.pathname === item.path 
                      ? 'bg-[#FFF5F3] text-[#5C3D36]' 
                      : 'text-[#9D8189] hover:bg-[#F8EDEB]'
                  }`}
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                >
                  {item.icon === 'logo' ? (
                    <div className={`relative w-8 h-8 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center`}>
                      <img src={logoImg} alt="" className="w-5 h-5 object-contain" aria-hidden="true" />
                      {((item.path === '/dashboard/messages' && messagesUnread > 0) || (item.path === '/dashboard/live-chat' && liveChatUnread > 0)) && (
                        <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center" aria-hidden="true">
                          <span className="absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75 animate-ping" />
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-white" />
                        </span>
                      )}
                    </div>
                  ) : item.icon === 'mamalink-logo' ? (
                    // 🎯 Élodie — Logo MamaLink blanc (identique tuile home) pour "Mon Match"
                    <div className={`relative w-8 h-8 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center`}>
                      <img
                        src={logoImg}
                        alt=""
                        className="w-5 h-5 object-contain"
                        style={{ filter: 'brightness(0) invert(1)' }}
                        aria-hidden="true"
                        data-testid="menu-mon-match-logo-mobile"
                      />
                    </div>
                  ) : item.emoji ? (
                    <div className={`relative w-8 h-8 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center`}>
                      <span className="text-lg leading-none" aria-hidden="true">{buildPersonalizedEmoji(item.emoji, emojiPrefs)}</span>
                      {((item.path === '/dashboard/messages' && messagesUnread > 0) || (item.path === '/dashboard/live-chat' && liveChatUnread > 0)) && (
                        <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center" aria-hidden="true">
                          <span className="absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75 animate-ping" />
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-white" />
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className={`relative w-8 h-8 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center`}>
                      <item.icon className="w-4 h-4 text-white" strokeWidth={2} aria-hidden="true" />
                      {((item.path === '/dashboard/messages' && messagesUnread > 0) || (item.path === '/dashboard/live-chat' && liveChatUnread > 0)) && (
                        <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center" aria-hidden="true" data-testid={item.path === '/dashboard/live-chat' ? 'mobile-livechat-online-dot' : 'mobile-messages-online-dot'}>
                          <span className="absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75 animate-ping" />
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-white" />
                        </span>
                      )}
                    </div>
                  )}
                  <span className="font-medium text-sm flex-1 text-left">{item.label}</span>
                  {/* 🔴 Élodie #791 — Pastille rouge "réponse admin non lue" sur Support + Messages */}
                  {supportUnread > 0 && (item.path === '/dashboard/help' || item.path === '/dashboard/messages') && (
                    <span
                      className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm"
                      aria-label={`${supportUnread} réponse(s) support non lue(s)`}
                      data-testid={`sidebar-support-badge-${item.path.split('/').pop()}`}
                    >
                      {supportUnread > 9 ? '9+' : supportUnread}
                    </span>
                  )}
                  {/* 🔴 Élodie #1084 — Pastille rose messages privés non lus sur Messages */}
                  {messagesUnread > 0 && item.path === '/dashboard/messages' && (
                    <span
                      className="min-w-[20px] h-5 px-1.5 rounded-full bg-gradient-to-r from-[#F5A5C4] to-[#E88AB0] text-white text-[10px] font-bold flex items-center justify-center shadow-sm"
                      aria-label={`${messagesUnread} message(s) non lu(s)`}
                      data-testid="sidebar-messages-unread-badge"
                    >
                      {messagesUnread > 9 ? '9+' : messagesUnread}
                    </span>
                  )}
                </button>
              ))}
            </nav>
            
            {/* Message for charter accepted */}
            {user?.charter_accepted && (
              <div className="mt-4 p-3 bg-gradient-to-r from-[#FFD700]/10 to-[#FFE066]/10 rounded-xl">
                <p className="text-[#5C3D36] text-xs text-center">
                  ⭐ Super maman engagée qui respecte la charte ! 💕
                </p>
              </div>
            )}
            
            <hr className="my-4 border-[#F8EDEB]" aria-hidden="true" />
            <button
              onClick={() => { logout(); closeMobileMenu(); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-[#D4A5A5] hover:bg-[#FDE2E4]"
              data-testid="mobile-logout-btn"
              aria-label="Se déconnecter"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />
              <span className="font-medium">{t('logout')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-[#F8EDEB] flex-col p-6" role="navigation" aria-label="Navigation principale">
        <div 
          className="flex items-center gap-3 mb-8 cursor-pointer" 
          onClick={() => navigate('/dashboard')}
          data-testid="sidebar-logo-link"
          role="link"
          aria-label="Aller à l'accueil"
        >
          <img src={logoImg} alt="MamaLink" className="w-14 h-14 object-contain" />
          <div>
            <h1 className="font-bold text-[#5C3D36] font-serif">MamaLink</h1>
            <p className="text-sm text-[#9D8189]">Bonjour, {user?.first_name}</p>
          </div>
        </div>

        <nav className="flex-1 space-y-2 overflow-y-auto">
          {navItems.map(item => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all ${
                location.pathname === item.path 
                  ? 'bg-[#FFF5F3] text-[#5C3D36]' 
                  : 'text-[#9D8189] hover:bg-[#F8EDEB]'
              }`}
            >
              {item.icon === 'logo' ? (
                <div className={`relative w-8 h-8 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center`}>
                  <img src={logoImg} alt="" className="w-5 h-5 object-contain" />
                  {((item.path === '/dashboard/messages' && messagesUnread > 0) || (item.path === '/dashboard/live-chat' && liveChatUnread > 0)) && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center">
                      <span className="absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-white" />
                    </span>
                  )}
                </div>
              ) : item.icon === 'mamalink-logo' ? (
                // 🎯 Élodie — Logo MamaLink blanc (identique tuile home) pour "Mon Match"
                <div className={`relative w-8 h-8 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center`}>
                  <img
                    src={logoImg}
                    alt=""
                    className="w-5 h-5 object-contain"
                    style={{ filter: 'brightness(0) invert(1)' }}
                    data-testid="menu-mon-match-logo-desktop"
                  />
                </div>
              ) : item.emoji ? (
                <div className={`relative w-8 h-8 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center`}>
                  <span className="text-lg leading-none">{buildPersonalizedEmoji(item.emoji, emojiPrefs)}</span>
                  {((item.path === '/dashboard/messages' && messagesUnread > 0) || (item.path === '/dashboard/live-chat' && liveChatUnread > 0)) && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center">
                      <span className="absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-white" />
                    </span>
                  )}
                </div>
              ) : (
                <div className={`relative w-8 h-8 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center`}>
                  <item.icon className="w-4 h-4 text-white" strokeWidth={2} />
                  {((item.path === '/dashboard/messages' && messagesUnread > 0) || (item.path === '/dashboard/live-chat' && liveChatUnread > 0)) && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center" data-testid={item.path === '/dashboard/live-chat' ? 'desktop-livechat-online-dot' : 'desktop-messages-online-dot'}>
                      <span className="absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-white" />
                    </span>
                  )}
                </div>
              )}
              <span className="font-medium text-sm flex-1 text-left">{item.label}</span>
              {/* 🔴 Élodie #791 — Pastille rouge "réponse admin non lue" sur Support + Messages */}
              {supportUnread > 0 && (item.path === '/dashboard/help' || item.path === '/dashboard/messages') && (
                <span
                  className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm"
                  aria-label={`${supportUnread} réponse(s) support non lue(s)`}
                  data-testid={`sidebar-support-badge-desktop-${item.path.split('/').pop()}`}
                >
                  {supportUnread > 9 ? '9+' : supportUnread}
                </span>
              )}
              {/* 🔴 Élodie #1084 — Pastille rose messages privés non lus */}
              {messagesUnread > 0 && item.path === '/dashboard/messages' && (
                <span
                  className="min-w-[20px] h-5 px-1.5 rounded-full bg-gradient-to-r from-[#F5A5C4] to-[#E88AB0] text-white text-[10px] font-bold flex items-center justify-center shadow-sm"
                  aria-label={`${messagesUnread} message(s) non lu(s)`}
                  data-testid="sidebar-messages-unread-badge-desktop"
                >
                  {messagesUnread > 9 ? '9+' : messagesUnread}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Message for charter accepted */}
        {user?.charter_accepted && (
          <div className="mt-4 p-3 bg-gradient-to-r from-[#FFD700]/10 to-[#FFE066]/10 rounded-xl">
            <p className="text-[#5C3D36] text-xs text-center">
              ⭐ Super maman engagée qui respecte la charte ! 💕
            </p>
          </div>
        )}

        <div className="space-y-2 pt-4 border-t border-[#F8EDEB]">
          <LanguageSelector />
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-[#D4A5A5] hover:bg-[#FDE2E4]"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            <span className="font-medium">{t('logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      {/* Pour les pages "full-height" (chat privé, chat support, communauté, médias),
          on retire le pt-20 et min-h-screen qui sortent le bas de l'écran sous les touches Android. */}
      <main
        id="main-content"
        className={`lg:ml-64 overflow-x-hidden ${
          /^\/dashboard\/(messages\/|support$|community$|live-chat$|media-gallery)/.test(location.pathname)
            ? 'pt-0 h-[100dvh]'
            : 'pt-20 lg:pt-0 min-h-screen'
        }`}
        role="main"
        aria-label="Contenu principal"
      >
        <NotificationContext.Provider value={{ unreadNotifications, markAllAsRead, refreshNotifications: fetchUnreadCount }}>
          {children}
        </NotificationContext.Provider>
      </main>

      {/* Reminder Modals for Rating and Sharing */}
      <ReminderModals userCreatedAt={user?.created_at} />

      {/* 🔔 Élodie — Carte in-app d'explication des notifications (après login,
          jamais en auto au page load, pas de prompt navigateur sans clic) */}
      <NotificationPermissionCard token={token} />

      {/* Contextual Upsell Modal (déclenché globalement via triggerUpsell()) */}
      <ContextualUpsellModal />
      
      {/* Onboarding Tutorial for first-time users */}
      {showOnboarding && (
        <OnboardingTutorial 
          user={user} 
          onComplete={handleOnboardingComplete} 
        />
      )}

      {/* 🎁 Élodie #1095 — Modal de bienvenue Pack Bienvenue MamaLink (1 seule fois) */}
      {starterPack && starterPack.is_active && !starterPack.seen_welcome && starterPack.days_left > 0 && (
        <StarterPackWelcomeModal
          open={true}
          daysLeft={starterPack.days_left}
          firstName={user?.first_name}
          onClose={async () => {
            try {
              await axios.post(`${BACKEND_URL}/api/starter-pack/dismiss-welcome`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
            } catch { /* ignore */ }
            setStarterPack(prev => prev ? { ...prev, seen_welcome: true } : prev);
          }}
          onGoToProfile={async () => {
            try {
              await axios.post(`${BACKEND_URL}/api/starter-pack/dismiss-welcome`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
            } catch { /* ignore */ }
            setStarterPack(prev => prev ? { ...prev, seen_welcome: true } : prev);
            navigate('/dashboard/profile');
          }}
          onGoToChat={async () => {
            try {
              await axios.post(`${BACKEND_URL}/api/starter-pack/dismiss-welcome`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
            } catch { /* ignore */ }
            setStarterPack(prev => prev ? { ...prev, seen_welcome: true } : prev);
            navigate('/dashboard/live-chat');
          }}
        />
      )}
    </div>
  );
};

// Dashboard Home
// 🎨 Emoji personnalisé — construit un emoji 👶 / 👩 avec skin tone & hair
// Skin modifiers Unicode : 🏻 (light), 🏼 (medium-light), 🏽 (medium), 🏾 (medium-dark), 🏿 (dark)
// Base emojis pour la maman : 👩 (défaut, cheveux bruns/noirs) ou 👱‍♀️ (blond)
// Modifiers cheveux (ZWJ) : 🦰 (roux), 🦱 (bouclés), 🦳 (blancs), 🦲 (chauve)
const SKIN_TONES = [
  { key: '',       label: '☀️',  emoji: '' },
  { key: 'light',  label: '🏻', emoji: '\u{1F3FB}' },
  { key: 'ml',     label: '🏼', emoji: '\u{1F3FC}' },
  { key: 'medium', label: '🏽', emoji: '\u{1F3FD}' },
  { key: 'md',     label: '🏾', emoji: '\u{1F3FE}' },
  { key: 'dark',   label: '🏿', emoji: '\u{1F3FF}' },
];
// Chaque style de cheveux définit soit un base emoji alternatif (blond) soit un modifier ZWJ (roux, bouclés, blancs, chauve).
// "brown" est le défaut naturel de 👩 (noir/brun selon la plateforme).
const HAIR_STYLES = [
  { key: '',      label: 'Brun',   base: '\u{1F469}', mod: '' },                              // 👩 défaut (brun/noir)
  { key: 'blond', label: 'Blond',  base: '\u{1F471}', mod: '\u200D\u2640\uFE0F' },            // 👱‍♀️ blond
  { key: 'red',   label: 'Roux',   base: '\u{1F469}', mod: '\u200D\u{1F9B0}' },               // 👩‍🦰
  { key: 'curly', label: 'Bouclés',base: '\u{1F469}', mod: '\u200D\u{1F9B1}' },               // 👩‍🦱
  { key: 'white', label: 'Blancs', base: '\u{1F469}', mod: '\u200D\u{1F9B3}' },               // 👩‍🦳
  { key: 'bald',  label: 'Chauve', base: '\u{1F469}', mod: '\u200D\u{1F9B2}' },               // 👩‍🦲
];

const buildPersonalizedEmoji = (kind, prefs) => {
  const skin = SKIN_TONES.find(s => s.key === (prefs?.skin || ''))?.emoji || '';
  if (kind === 'baby') {
    // 👶 Le bébé n'accepte que le modificateur de peau — jamais de cheveux
    return '\u{1F476}' + skin;
  }
  if (kind === 'mom') {
    // Femme (base 👩 ou 👱) + skin + hair modifier (ZWJ)
    const hairStyle = HAIR_STYLES.find(h => h.key === (prefs?.hair || '')) || HAIR_STYLES[0];
    return hairStyle.base + skin + hairStyle.mod;
  }
  return kind;
};

const EmojiPersonalizerModal = ({ prefs, onSave, onClose }) => {
  const [skin, setSkin] = useState(prefs?.skin || '');
  const [hair, setHair] = useState(prefs?.hair || '');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-3" onClick={onClose} data-testid="emoji-personalize-modal">
      <div className="bg-white rounded-3xl p-5 w-full max-w-sm shadow-2xl relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-[#F8EDEB] text-[#9D8189]" data-testid="close-emoji-picker">
          ✕
        </button>
        <div className="text-center mb-4">
          <div className="flex items-end justify-center gap-3">
            <div className="text-6xl" data-testid="preview-mom">
              {buildPersonalizedEmoji('mom', { skin, hair })}
            </div>
            <div className="text-5xl mb-1" data-testid="preview-baby">
              {buildPersonalizedEmoji('baby', { skin })}
            </div>
          </div>
          <h3 className="text-[#5C3D36] font-serif font-bold text-lg mt-3">Personnalise tes emojis 🎨</h3>
          <p className="text-[10px] text-[#9D8189]">Représente-toi avec ton bébé comme tu le sens 💕</p>
        </div>

        {/* Section couleur de peau — partagée entre toi et bébé */}
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-wide text-[#9D8189] font-bold mb-2">
            👩 + 👶 Couleur de peau
          </p>
          <div className="flex justify-between gap-1.5">
            {SKIN_TONES.map(s => (
              <button
                key={s.key || 'default'}
                onClick={() => setSkin(s.key)}
                className={`flex-1 aspect-square rounded-2xl text-2xl flex items-center justify-center transition-all ${
                  skin === s.key ? 'bg-[#FFF6E0] border-2 border-[#F5B841] scale-110' : 'bg-[#FAFAFA] border-2 border-transparent hover:bg-[#FFF9F7]'
                }`}
                data-testid={`skin-${s.key || 'default'}`}
                title={s.label}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Section cheveux — uniquement pour la maman */}
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-wide text-[#9D8189] font-bold mb-2">
            👩 Cheveux (pour toi uniquement)
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {HAIR_STYLES.map(h => (
              <button
                key={h.key || 'default'}
                onClick={() => setHair(h.key)}
                className={`py-2 rounded-2xl text-center transition-all ${
                  hair === h.key ? 'bg-[#FFF6E0] border-2 border-[#F5B841]' : 'bg-[#FAFAFA] border-2 border-transparent hover:bg-[#FFF9F7]'
                }`}
                data-testid={`hair-${h.key || 'default'}`}
              >
                <div className="text-2xl">{buildPersonalizedEmoji('mom', { skin, hair: h.key })}</div>
                <div className="text-[9px] text-[#9D8189] mt-0.5 font-semibold">{h.label}</div>
              </button>
            ))}
          </div>
          <p className="text-[9px] text-[#9D8189] italic mt-2 text-center">
            Bébé garde son emoji naturel 👶 (le rendu dépend de ton téléphone).
          </p>
        </div>

        <button
          onClick={() => { onSave({ skin, hair }); onClose(); }}
          className="w-full py-3 rounded-full bg-gradient-to-r from-[#FFB5A7] to-[#FF8FA3] text-white font-bold text-sm shadow-sm active:scale-95 transition-transform"
          data-testid="save-emoji-prefs-btn"
        >
          Enregistrer 💕
        </button>
      </div>
    </div>
  );
};

const DashboardHome = () => {
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const [dailyMessage, setDailyMessage] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(true);
  const [selectedMood, setSelectedMood] = useState(null);
  // 🏠 Élodie #793 — Compteurs d'activité sur les tuiles (forum, tips, events, needs)
  const [activityCounts, setActivityCounts] = useState({ forum: 0, tips: 0, events: 0, needs: 0 });
  const activityStorageKey = user?.id ? `mamalink_activity_seen_${user.id}` : 'mamalink_activity_seen';
  const readLastSeen = useCallback(() => {
    try { return JSON.parse(localStorage.getItem(activityStorageKey) || '{}'); }
    catch { return {}; }
  }, [activityStorageKey]);
  const writeLastSeen = useCallback((patch) => {
    const cur = readLastSeen();
    const next = { ...cur, ...patch };
    try { localStorage.setItem(activityStorageKey, JSON.stringify(next)); } catch { /* noop */ }
  }, [readLastSeen, activityStorageKey]);
  const fetchActivityCounts = useCallback(async () => {
    if (!token) return;
    const seen = readLastSeen();
    // Fallback: si jamais vu, on considère "vu à l'instant de l'inscription/1er accueil"
    //           pour ne pas montrer 99+ sur la 1ère ouverture.
    const now = new Date().toISOString();
    const params = new URLSearchParams();
    ['forum', 'tips', 'events', 'needs'].forEach((k) => {
      const iso = seen[k];
      if (iso) params.set(`since_${k}`, iso);
      else {
        // Premier accès jamais → on marque "maintenant" et on retourne 0
        writeLastSeen({ [k]: now });
      }
    });
    // Si aucune clé n'a de since_*, ça retourne 0 partout — OK.
    try {
      const r = await axios.get(`${BACKEND_URL}/api/home/activity-counts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityCounts({
        forum: r.data?.forum || 0,
        tips: r.data?.tips || 0,
        events: r.data?.events || 0,
        needs: r.data?.needs || 0,
      });
    } catch { /* silent */ }
  }, [token, readLastSeen, writeLastSeen]);
  useEffect(() => {
    fetchActivityCounts();
    const iv = setInterval(fetchActivityCounts, 60000); // refresh toutes les 60s
    return () => clearInterval(iv);
  }, [fetchActivityCounts]);
  // Handler : au clic sur une tuile qui a un badge, on marque comme vue + reset local
  const markSectionSeen = useCallback((badgeKey) => {
    if (!badgeKey) return;
    writeLastSeen({ [badgeKey]: new Date().toISOString() });
    setActivityCounts(prev => ({ ...prev, [badgeKey]: 0 }));
  }, [writeLastSeen]);
  // 💬 Phrase adaptative affichée sous les emojis (persistée jusqu'à minuit)
  const [moodPhrase, setMoodPhrase] = useState('');
  // 💞 Compteur d'humeurs basses consécutives → après 3 on propose les numéros d'urgence
  const [showEmergencyHint, setShowEmergencyHint] = useState(false);
  // 🎨 Préférences d'emoji personnalisées (skin/hair) — persistées en localStorage par user
  const emojiPrefsKey = user?.id ? `mamalink_emoji_prefs_${user.id}` : 'mamalink_emoji_prefs';
  const [emojiPrefs, setEmojiPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(emojiPrefsKey);
      return raw ? JSON.parse(raw) : { skin: '', hair: '' };
    } catch { return { skin: '', hair: '' }; }
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const saveEmojiPrefs = (next) => {
    setEmojiPrefs(next);
    try {
      localStorage.setItem(emojiPrefsKey, JSON.stringify(next));
      // Notifie DashboardLayout (menu déroulant) et BabyPlanner pour refresh emoji
      window.dispatchEvent(new Event('mamalink-emoji-prefs-updated'));
    } catch { /* ignore */ }
  };
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check if mood should be reset (every day at 6am)
  // 🔑 Toutes les clés mood sont scopées par user.id pour éviter le partage
  // entre comptes sur un même appareil (bug remonté par Élodie : changer l'humeur
  // sur son compte modifiait aussi l'humeur du compte MamaLink admin).
  const moodKey = (suffix) => user?.id ? `mamalink_mood_${suffix}_${user.id}` : `mamalink_mood_${suffix}`;

  useEffect(() => {
    if (!user?.id) return;  // Attend que l'utilisatrice soit chargée
    const checkMoodReset = () => {
      const savedMood = localStorage.getItem(moodKey('label'));
      const savedDate = localStorage.getItem(moodKey('date'));
      const savedPhrase = localStorage.getItem(moodKey('phrase'));

      if (savedMood && savedDate) {
        const now = new Date();
        const savedDateTime = new Date(savedDate);

        // Get today's 6am
        const today6am = new Date();
        today6am.setHours(6, 0, 0, 0);

        // If saved before today's 6am and now is after 6am, reset
        if (savedDateTime < today6am && now >= today6am) {
          localStorage.removeItem(moodKey('label'));
          localStorage.removeItem(moodKey('date'));
          localStorage.removeItem(moodKey('phrase'));
          setSelectedMood(null);
          setMoodPhrase('');
          setShowEmergencyHint(false);
        } else {
          setSelectedMood(savedMood);
          if (savedPhrase) setMoodPhrase(savedPhrase);
          // Charge aussi l'état "emergency hint" si déjà 3+ humeurs basses consécutives
          const streak = parseInt(localStorage.getItem(moodKey('bad_streak')) || '0', 10);
          if (streak >= 3) setShowEmergencyHint(true);
          else setShowEmergencyHint(false);
        }
      } else {
        // Aucun mood pour CE user : reset les states (changement de compte)
        setSelectedMood(null);
        setMoodPhrase('');
        setShowEmergencyHint(false);
      }
    };

    // 🧹 Nettoie une seule fois les anciennes clés non-scopées (migration v1 → v2)
    // pour éviter qu'elles continuent de polluer un compte fraîchement scopé.
    ['mamalink_daily_mood', 'mamalink_mood_date', 'mamalink_mood_phrase', 'mamalink_mood_bad_streak'].forEach(k => {
      if (localStorage.getItem(k) !== null) localStorage.removeItem(k);
    });

    checkMoodReset();
  }, [user?.id]);

  useEffect(() => {
    const fetchDailyMessage = async () => {
      try {
        const response = await axios.get(`${API}/daily-message`);
        setDailyMessage(response.data);
      } catch (error) {
        console.error('Error fetching daily message:', error);
      } finally {
        setLoadingMessage(false);
      }
    };
    fetchDailyMessage();
  }, []);

  // 💬 Banque de phrases adaptatives par humeur (10 par catégorie pour rotation)
  const MOOD_PHRASES = {
    'Super': [
      'Passe une merveilleuse journée, super maman ✨',
      "Belle journée à toi, tu illumines tout autour 🌟",
      "Profite à fond, tu le mérites 💫",
      'Quel bonheur de te voir rayonner aujourd\'hui 🌸',
      'Continue comme ça, super maman, tu fais des merveilles ⭐',
      'Une journée pleine de douceur t\'attend 💖',
      'Tu débordes d\'amour aujourd\'hui, savoure 🌟',
      'Belle énergie maman, ça fait plaisir à voir ✨',
      'Garde précieusement cette belle humeur 💕',
      'Magnifique, profite de chaque instant 🌟',
    ],
    'Ça va': [
      'Une belle journée tranquille, super maman 🌸',
      "Doucement mais sûrement, tu avances bien 💕",
      "Prends soin de toi aujourd'hui, tu le mérites 🌿",
      'Une journée à ton rythme, c\'est parfait 💗',
      'Chaque petit pas compte, super maman 🌷',
      'Tu fais ce que tu peux, et c\'est déjà beau 💞',
      'Respire, savoure les petites choses 🌼',
      'Continue à avancer, tout en douceur 💝',
      'Tu es exactement où il faut être aujourd\'hui 🌸',
      'On t\'envoie plein de bonnes ondes 💖',
    ],
    'Bof': [
      'Ne t\'inquiète pas, il y aura des jours meilleurs, super maman 💕',
      'Une mauvaise journée ne fait pas une mauvaise maman 💗',
      'C\'est normal d\'avoir des hauts et des bas, tu fais de ton mieux 💞',
      'Demain est un nouveau jour, super maman 🌸',
      'Prends un instant pour toi, tu en as besoin 💖',
      'Tu n\'es pas seule, on est là 💕',
      'Sois douce envers toi-même aujourd\'hui 🌷',
      'Ça va passer, et tu seras plus forte encore 💗',
      'Une petite pause, un petit thé, et ça ira mieux 🍵',
      'Ton cœur est grand, même les jours difficiles 💞',
    ],
    'Difficile': [
      'N\'oublie pas que c\'est passager, ça va aller mieux. Tu es une super maman, tu fais de ton mieux 💕',
      'Tu es plus forte que tu ne le penses, super maman 💗',
      'Ce moment est dur, mais il ne durera pas. On est là pour toi 💞',
      'Respire, doucement. Une chose à la fois 🌸',
      'Tu fais déjà énormément, sois fière de toi 💖',
      'Les jours sombres rendent les jours lumineux plus beaux ✨',
      'Tu n\'as pas à porter tout ça seule. On est avec toi 💕',
      'Chaque petite victoire compte, même la plus minuscule 🌷',
      'Tu es aimée, tu es importante, tu es une super maman 💗',
      'Sois douce avec toi-même, comme tu l\'es avec ton bébé 💞',
    ],
  };

  // 💬 Pioche une phrase aléatoire dans le pool de l'humeur, en évitant la précédente
  const pickPhrase = (moodLabel) => {
    const pool = MOOD_PHRASES[moodLabel] || [];
    if (pool.length === 0) return '';
    const previous = localStorage.getItem(moodKey('phrase')) || '';
    const candidates = pool.filter(p => p !== previous);
    const finalPool = candidates.length > 0 ? candidates : pool;
    return finalPool[Math.floor(Math.random() * finalPool.length)];
  };

  const handleMoodSelect = async (mood) => {
    setSelectedMood(mood.label);
    const newPhrase = pickPhrase(mood.label);
    setMoodPhrase(newPhrase);
    localStorage.setItem(moodKey('label'), mood.label);
    localStorage.setItem(moodKey('date'), new Date().toISOString());
    localStorage.setItem(moodKey('phrase'), newPhrase);

    // 💞 Compteur d'humeurs "Difficile" consécutives (réinitialisé si autre humeur)
    let badCount = parseInt(localStorage.getItem(moodKey('bad_streak')) || '0', 10);
    if (mood.label === 'Difficile') {
      badCount += 1;
    } else {
      badCount = 0;
    }
    localStorage.setItem(moodKey('bad_streak'), String(badCount));
    setShowEmergencyHint(badCount >= 3);
    
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mood`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mood: mood.label })
      });
    } catch (e) {
      // silent
    }
  };

  return (
    <div className="p-6 lg:p-12 max-w-6xl mx-auto">
      <div className="animate-fade-in">
        {/* 🎁 Élodie #1095 — Bandeau Pack Bienvenue (au-dessus du Bonjour) */}
        <StarterPackHomeBanner />
        {/* 🌸 Teaser lancement Baby & Mom Planner (Élodie #773) — retiré auto le 26 juillet 2026 */}
        <PlannerLaunchTeaser />
        <h1 className="text-3xl md:text-4xl font-bold text-[#5C3D36] font-serif mb-2">
          Bonjour, {user?.first_name} 
        </h1>
        <p className="text-[#9D8189] text-lg mb-3">Comment vas-tu aujourd'hui ?</p>
        
        {/* Quick Mood Response */}
        <div className="flex gap-3 mb-8">
          {[
            { emoji: '😊', label: 'Super', color: 'bg-[#A8E6CF]' },
            { emoji: '🙂', label: 'Ça va', color: 'bg-[#A2D2FF]' },
            { emoji: '😐', label: 'Bof', color: 'bg-[#FFE5B4]' },
            { emoji: '😔', label: 'Difficile', color: 'bg-[#FFB5A7]' },
          ].map((mood) => {
            const isSelected = selectedMood === mood.label;
            const hasSelection = selectedMood !== null;
            
            return (
              <button
                key={mood.label}
                onClick={() => handleMoodSelect(mood)}
                className={`${mood.color} transition-all duration-300 w-11 h-11 rounded-full flex items-center justify-center shadow-sm
                  ${isSelected 
                    ? 'scale-125 ring-4 ring-white ring-offset-2 ring-offset-[#FFF9F5] shadow-lg z-10' 
                    : hasSelection 
                      ? 'opacity-40 scale-90' 
                      : 'hover:scale-110'
                  }`}
                title={mood.label}
              >
                <span className={`text-2xl transition-transform duration-300 ${isSelected ? 'scale-110' : ''}`}>
                  {mood.emoji}
                </span>
              </button>
            );
          })}
        </div>

        {/* 💬 Phrase adaptative + animation selon l'humeur */}
        {selectedMood && moodPhrase && (
          <div
            className={`relative mood-message-card mood-${selectedMood === 'Super' ? 'super' : selectedMood === 'Ça va' ? 'cava' : selectedMood === 'Bof' ? 'bof' : 'difficile'} mb-4 animate-fade-in`}
            data-testid={`mood-phrase-${selectedMood}`}
          >
            {/* Animation overlay */}
            <div className="mood-anim-layer" aria-hidden="true">
              {(selectedMood === 'Super' || selectedMood === 'Ça va') ? (
                <>
                  <span className="mood-star" style={{ left: '8%', top: '20%', animationDelay: '0s' }}>✨</span>
                  <span className="mood-star" style={{ left: '32%', top: '60%', animationDelay: '0.6s' }}>⭐</span>
                  <span className="mood-star" style={{ left: '58%', top: '15%', animationDelay: '1.2s' }}>✨</span>
                  <span className="mood-star" style={{ left: '82%', top: '55%', animationDelay: '1.8s' }}>🌟</span>
                  <span className="mood-star" style={{ left: '70%', top: '80%', animationDelay: '2.4s' }}>✨</span>
                </>
              ) : (
                <>
                  <span className="mood-heart" style={{ left: '10%', animationDelay: '0s' }}>💕</span>
                  <span className="mood-heart" style={{ left: '28%', animationDelay: '1.2s' }}>💗</span>
                  <span className="mood-heart" style={{ left: '50%', animationDelay: '0.6s' }}>💖</span>
                  <span className="mood-heart" style={{ left: '72%', animationDelay: '1.8s' }}>💞</span>
                  <span className="mood-heart" style={{ left: '88%', animationDelay: '2.4s' }}>💕</span>
                </>
              )}
            </div>
            <p className="relative z-10 text-[#5C3D36] text-base font-medium leading-relaxed">
              {moodPhrase}
            </p>
          </div>
        )}

        {/* 🆘 Alerte douce si 3 humeurs "Difficile" consécutives */}
        {showEmergencyHint && (
          <div
            className="bg-gradient-to-r from-[#FCE4EC] via-[#FFD6E0] to-[#FCE4EC] border-2 border-[#FFB5A7]/40 rounded-2xl p-4 mb-4 animate-fade-in"
            data-testid="mood-emergency-hint"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl flex-shrink-0">🫶</span>
              <div className="flex-1">
                <p className="text-sm text-[#5C3D36] leading-relaxed">
                  N'hésite pas à aller regarder les numéros dans la rubrique{' '}
                  <button
                    onClick={() => navigate('/dashboard/urgences')}
                    className="font-bold text-[#FFB5A7] underline underline-offset-2"
                    data-testid="mood-emergency-link"
                  >
                    Urgence
                  </button>
                  {' '}et fais-toi aider si tu en as besoin. Tu n'es pas seule, super maman 💕
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Daily Message - avec gradient sunrise et animation */}
      <div className="mb-4" style={{ minHeight: loadingMessage ? '120px' : 'auto' }}>
        {loadingMessage ? (
          <div className="daily-message-card animate-pulse">
            <div className="h-6 bg-white/20 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-white/20 rounded w-1/4 mt-4"></div>
          </div>
        ) : dailyMessage ? (
          <div className="daily-message-card sunrise-gradient animate-fade-in floating-particles">
            <p className="text-lg font-medium relative z-10">
              {language === 'en' ? dailyMessage.content_en : dailyMessage.content}
            </p>
            <p className="text-sm opacity-70 mt-4 font-accent text-xl">{t('dailyMessage')}</p>
          </div>
        ) : null}
      </div>

      {/* 🌸 Élodie #1096 — Nouvelles mamans à accueillir */}
      <NewMembersWidget />

      {/* Onboarding Checklist (visible jusqu'à completion ou dismiss) */}
      <OnboardingChecklist />

      {/* Quick Actions - hauteur fixe pour éviter le sautillement */}
      {/* Dégradé arc-en-ciel : Rose → Violet → Bleu → Vert → Jaune → Orange → Corail */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {[
          { icon: Baby, emoji: 'baby', label: 'Baby Planner', path: '/dashboard/baby-planner', color: 'from-[#FFB5A7] via-[#FFC8B8] to-[#FCD5CE]', tint: '#FFB5A7' },     // 1. Rose pastel bébé
          { icon: Sparkles, emoji: 'mom', label: 'Mom Planner', path: '/dashboard/mom-planner', color: 'from-[#E0BBE4] via-[#E8CCE9] to-[#F0D5F5]', tint: '#E0BBE4' },   // 2. Mauve maman
          { icon: 'mamalink-logo', label: 'Mon Match', path: '/dashboard/my-match', color: 'from-[#FFC8DD] via-[#FFD4E5] to-[#FFDEEB]', tint: '#FFC8DD' },           // 3. Rose bonbon (chaud)
          { icon: MessageCircle, label: t('forum'), path: '/dashboard/forum', color: 'from-[#CDB4DB] via-[#D8C4E3] to-[#E2D1F9]', tint: '#CDB4DB', badgeKey: 'forum' },   // 4. Violet lavande
          { icon: Sparkles, label: 'Astuces & Conseils', path: '/dashboard/tips-advice', color: 'from-[#A2D2FF] via-[#B5DBFF] to-[#BDE0FE]', tint: '#A2D2FF', badgeKey: 'tips' }, // 5. Bleu ciel
          { icon: Calendar, label: 'Événements', path: '/dashboard/events', color: 'from-[#90E0EF] via-[#A8E8F4] to-[#CAF0F8]', tint: '#90E0EF', badgeKey: 'events' },     // 6. Bleu turquoise
          { icon: Users, label: 'J\'ai besoin de', path: '/dashboard/service-requests', color: 'from-[#98D8C8] via-[#ACE0D4] to-[#B8E8DC]', tint: '#98D8C8', badgeKey: 'needs' }, // 7. Turquoise menthe
          { icon: MapPin, label: 'Autour de moi', path: '/dashboard/nearby', color: 'from-[#B5E48C] via-[#C6EBA4] to-[#D8F3DC]', tint: '#B5E48C' },    // 8. Vert pomme
          { icon: MessageSquare, label: 'Témoignages', path: '/dashboard/testimonies', color: 'from-[#D9ED92] via-[#E4F1A8] to-[#EEF5BE]', tint: '#D9ED92' }, // 9. Vert-jaune
          { icon: Tag, label: 'Bons Plans', path: '/dashboard/bons-plans', color: 'from-[#FFE066] via-[#FFEA8A] to-[#FFF3B0]', tint: '#FFE066' },      // 10. Jaune
          { icon: Brain, label: 'Quiz du Jour', path: '/dashboard/quiz', color: 'from-[#FFC873] via-[#FFD68A] to-[#FFE4A0]', tint: '#FFC873' },        // 11. Jaune-orange
          { icon: Phone, label: 'Urgences', path: '/dashboard/urgences', color: 'from-[#FF9A8B] via-[#FFAEA2] to-[#FFC2B8]', tint: '#FF9A8B' },        // 12. Corail
        ].map((item, index) => {
          const badgeCount = item.badgeKey ? activityCounts[item.badgeKey] : 0;
          return (
          <div
            key={item.path}
            role="button"
            tabIndex={0}
            onClick={() => { markSectionSeen(item.badgeKey); navigate(item.path); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); markSectionSeen(item.badgeKey); navigate(item.path); } }}
            className={`mama-card p-6 text-center mama-hover-lift shadow-card overflow-hidden gentle-fade-in gentle-fade-in-delay-${Math.min(index + 1, 4)} relative cursor-pointer`}
            style={{ height: '140px' }}
            data-testid={`quick-action-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-3 ${item.label === 'Mon Match' ? 'heart-beating' : ''}`}>
              {item.emoji ? (
                <span className="text-3xl" data-testid={`tile-emoji-${item.emoji}`} style={{ lineHeight: 1 }}>
                  {buildPersonalizedEmoji(item.emoji, emojiPrefs)}
                </span>
              ) : item.icon === 'mamalink-logo' ? (
                // 🎯 Élodie #1147 — Logo MamaLink blanc pour "Mon Match" (au lieu du cœur générique)
                <img
                  src={logoImg}
                  alt="Mon Match"
                  className="w-8 h-8 object-contain"
                  style={{ filter: 'brightness(0) invert(1)' }}
                  data-testid="mon-match-logo"
                />
              ) : (
                <item.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
              )}
            </div>
            <span className="font-medium text-[#5C3D36] text-sm leading-tight line-clamp-2">{item.label}</span>
            {item.emoji && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowEmojiPicker(true); }}
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/70 backdrop-blur-sm text-xs flex items-center justify-center hover:bg-white transition-colors"
                title="Personnaliser l'emoji"
                data-testid={`personalize-emoji-${item.emoji}`}
              >
                🎨
              </button>
            )}
            {/* 🏠 Élodie #793 — Badge d'activité discret pastel (rien affiché si 0) */}
            {badgeCount > 0 && (
              <span
                className={`absolute top-2 right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-gradient-to-br ${item.color} border border-white/70 text-[#5C3D36] text-[10px] font-bold flex items-center justify-center shadow-sm`}
                aria-label={`${badgeCount} nouveauté(s) depuis ta dernière visite`}
                data-testid={`home-activity-badge-${item.badgeKey}`}
                style={{ backdropFilter: 'blur(4px)' }}
              >
                {badgeCount > 9 ? '9+' : badgeCount}
              </span>
            )}
          </div>
          );
        })}
      </div>

      {/* 🎉 Élodie #1097 — Bannière lancement déplacée APRÈS la grille quiz/urgences */}
      <LaunchPromoBanner />

      {/* Match Status */}
      {!user?.has_completed_quiz && (
        <div className="mama-card bg-gradient-to-r from-[#FFB5A7]/20 to-[#FCD5CE]/20 border-2 border-[#FFB5A7] animate-fade-in">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FFB5A7] to-[#FCD5CE] flex items-center justify-center">
              <Heart className="w-10 h-10 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-[#5C3D36] font-serif mb-2">
                {t('findMatch')}
              </h3>
              <p className="text-[#9D8189] mb-4">
                Réponds à quelques questions pour trouver ta meilleure amie de maternité
              </p>
              <button 
                onClick={() => navigate('/dashboard/match')}
                className="mama-btn-primary"
                data-testid="start-quiz-btn"
              >
                Commencer le questionnaire
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Social Proof Banner — en bas de la page d'accueil */}
      <div className="mt-8">
        <SocialProofBanner />
      </div>

      {/* 🎨 Modal de personnalisation des emojis */}
      {showEmojiPicker && (
        <EmojiPersonalizerModal
          prefs={emojiPrefs}
          onSave={saveEmojiPrefs}
          onClose={() => setShowEmojiPicker(false)}
        />
      )}
    </div>
  );
};

// Export the pages that will be in separate files
export { 
  AuthProvider, 
  LanguageProvider, 
  useAuth, 
  useLanguage,
  useNotifications,
  useTheme,
  API, 
  translations,
  DashboardLayout,
  ProtectedRoute,
  LandingPage,
  LoginPage,
  RegisterPage,
  DashboardHome,
  LanguageSelector
};

// Public Legal Pages (accessible sans connexion)
const PublicPrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const PublicTermsOfService = React.lazy(() => import('./pages/TermsOfServicePage'));
const PublicLegalNotice = React.lazy(() => import('./pages/LegalNoticePage'));
const PublicSalesTerms = React.lazy(() => import('./pages/SalesTermsPage'));
const PublicDeleteAccount = React.lazy(() => import('./pages/PublicDeleteAccountPage'));
const PublicHelpPage = React.lazy(() => import('./pages/PublicHelpPage'));
const PublicChildSafety = React.lazy(() => import('./pages/ChildSafetyStandardsPage'));

// Pages publiques de vérification email
const VerifyEmailPage = React.lazy(() => import('./pages/VerifyEmailPage'));
const UnsubscribePage = React.lazy(() => import('./pages/UnsubscribePage'));
const PublicBirthListPage = React.lazy(() => import('./pages/PublicBirthListPage'));
const PublicBabyNeedsPage = React.lazy(() => import('./pages/PublicBabyNeedsPage'));
const PublicPartnerTryingPage = React.lazy(() => import('./pages/PublicPartnerTryingPage'));
const CheckEmailPage = React.lazy(() => import('./pages/CheckEmailPage'));

// Loading Component pour les transitions fluides
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-[#FFB5A7] border-t-transparent rounded-full animate-spin" />
  </div>
);

// App Component
function GlobalNotificationHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const seenCallIdRef = useRef(null);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
    const handler = (event) => {
      const msg = event.data;
      if (!msg || msg.type !== 'NOTIFICATION_CLICK') return;
      if (!msg.url) return;
      const finalUrl = msg.callAction
        ? msg.url + (msg.url.includes('?') ? '&' : '?') + 'auto_action=' + msg.callAction
        : msg.url;
      try {
        navigate(finalUrl);
      } catch (e) {
        window.location.href = finalUrl;
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  // Elodie #240297 - l'alerte d'appel entrant ne s'affichait qu'a l'interieur
  // d'une conversation deja ouverte (poll propre a ChatPage). Ce poll global
  // permet de detecter un appel entrant meme quand l'utilisatrice est sur un
  // autre ecran ou vient d'ouvrir l'app, en reutilisant l'endpoint deja fait
  // pour ca cote backend.
  useEffect(() => {
    if (!token) return undefined;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/calls/incoming/active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const call = res.data;
        if (!call || !call.call_id) return;
        if (user && call.caller_id === user.id) return;
        if (seenCallIdRef.current === call.call_id) return;
        if (location.pathname.startsWith(`/dashboard/messages/${call.conversation_id}`)) {
          // Deja sur la conversation concernee : le poll de ChatPage gere l'affichage.
          return;
        }
        seenCallIdRef.current = call.call_id;
        navigate(`/dashboard/messages/${call.conversation_id}?incoming_call=${call.call_id}`);
      } catch (e) {
        // silencieux
      }
    };

    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [token, user, location.pathname, navigate]);

  return null;
}

function App() {
  return (
    <ThemeProvider>
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <GlobalNotificationHandler />
          <AppConfirmHost />
          <Toaster 
            position="top-center" 
            toastOptions={{
              style: {
                background: 'white',
                color: '#5C3D36',
                borderRadius: '1rem',
                border: '1px solid #F8EDEB'
              }
            }}
          />
          <React.Suspense fallback={<div className="min-h-screen bg-[#FFF1E6] flex items-center justify-center"><PageLoader /></div>}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* ✉️ Vérification email */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/birth-list/:token" element={<PublicBirthListPage />} />
            <Route path="/baby-needs/:token" element={<PublicBabyNeedsPage />} />
            <Route path="/partner/trying/:token" element={<PublicPartnerTryingPage />} />
            <Route path="/check-email" element={<CheckEmailPage />} />
            {/* Pages légales publiques */}
            <Route path="/privacy-policy" element={<PublicPrivacyPolicy />} />
            <Route path="/privacy" element={<PublicPrivacyPolicy />} />
            <Route path="/terms-of-service" element={<PublicTermsOfService />} />
            <Route path="/terms" element={<PublicTermsOfService />} />
            <Route path="/legal-notice" element={<PublicLegalNotice />} />
            <Route path="/sales-terms" element={<PublicSalesTerms />} />
            <Route path="/cgv" element={<PublicSalesTerms />} />
            {/* 🛡️ Child Safety Standards (requis par Google Play — CSAE compliance) */}
            <Route path="/child-safety-standards" element={<PublicChildSafety />} />
            <Route path="/child-safety" element={<PublicChildSafety />} />
            <Route path="/normes-securite-enfants" element={<PublicChildSafety />} />
            {/* Suppression de compte publique (conformité Google Play Data Deletion) */}
            <Route path="/delete-account" element={<PublicDeleteAccount />} />
            <Route path="/supprimer-mon-compte" element={<PublicDeleteAccount />} />
            {/* 🌸 Aide publique (requise par Apple pour App Store review) */}
            <Route path="/aide" element={<PublicHelpPage />} />
            <Route path="/help" element={<PublicHelpPage />} />
            <Route path="/support" element={<PublicHelpPage />} />
            {/* 🩷 Élodie #778 — Route courte pour invitations co-parent (URL plus courte à partager) */}
            <Route path="/co/:token" element={<Navigate to={window.location.pathname.replace('/co/', '/dashboard/baby-planner/join/')} replace />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DashboardHome />
                </DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard/*" element={
              <ProtectedRoute>
                <DashboardLayout>
                  <React.Suspense fallback={<PageLoader />}>
                    <DashboardRoutes />
                  </React.Suspense>
                </DashboardLayout>
              </ProtectedRoute>
            } />
          </Routes>
          </React.Suspense>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
    </ThemeProvider>
  );
}

// Lazy load dashboard routes
const DashboardRoutes = React.lazy(() => import('./DashboardRoutes'));

export default App;
